/**
 * The admin-api contract: action names, what each takes and answers, and the
 * error codes. Plain TypeScript with no imports, so admin-api (Deno) and the
 * staff portal (Vite) both read this one file.
 *
 * Every call is `POST /functions/v1/admin-api` with `{ action, args }` and
 * the staff session's access token. The answer is `{ data }` on success and
 * `{ error: { code, message } }` otherwise, with a matching HTTP status.
 *
 * The permissions here are for the portal to show or hide a control. The
 * server decides: admin.handle checks the same permission, and each admin.*
 * function checks its own finer rules again.
 */

// ---------------------------------------------------------------------------
// Permissions and roles
// ---------------------------------------------------------------------------

export const PERMISSIONS = [
  'customers.view',
  'customers.reveal_phone',
  'customers.reveal_identity',
  'issues.view',
  'issues.work',
  'issues.propose',
  'issues.approve',
  'reversals.view',
  'reversals.propose',
  'reversals.approve',
  'adjustments.view',
  'adjustments.propose',
  'adjustments.approve',
  'adjustments.pick_source',
  'restrictions.view',
  'restrictions.propose',
  'restrictions.approve',
  'freeze.now',
  'money_ops.view',
  'money_ops.propose',
  'money_ops.approve',
  'fees.view',
  'fees.propose',
  'fees.approve',
  'limits.view',
  'limits.propose',
  'limits.approve',
  'killswitch.view',
  'killswitch.off',
  'killswitch.propose',
  'killswitch.approve',
  'settings.view',
  'settings.propose',
  'settings.approve',
  'staff.disable',
  'exports.own_cases',
  'exports.masked',
  'exports.unmasked_propose',
  'exports.unmasked_approve',
  'audit.own',
  'audit.all',
  'kyc.view',
  'kyc.decide',
  'referral_rewards.view',
  'referral_rewards.propose',
  'referral_rewards.approve',
  'referral_rewards.decline',
  'credits.view',
  'credits.propose',
  'credits.approve',
  'referrals.view',
  'referrals.void',
  'wall.view',
  'countries.manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export const ROLES = [
  'support',
  'operations',
  'finance',
  'compliance',
  'admin',
  'auditor',
  'wall_display',
] as const;

export type Role = (typeof ROLES)[number];

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export const ERROR_STATUS = {
  UNAUTHORIZED: 401,
  MFA_REQUIRED: 401,
  SESSION_ENDED: 401,
  SESSION_EXPIRED: 401,
  NOT_STAFF: 403,
  STAFF_DISABLED: 403,
  FORBIDDEN: 403,
  UNKNOWN_ACTION: 404,
  NOT_FOUND: 404,
  INVALID_REQUEST: 400,
  INVALID_EMAIL: 400,
  INVALID_NAME: 400,
  INVALID_REASON: 400,
  INVALID_QUERY: 400,
  INVALID_FILE: 400,
  INVALID_RULES: 400,
  INVALID_AMOUNT: 400,
  // Settling card payments against a pool credit (card_settlement).
  CARD_IDS_INVALID: 400,
  CARD_LINE_MISSING: 404,
  CARD_LINE_NOT_CREDIT: 409,
  CARD_LINE_CLASS: 409,
  CARD_LINE_DUPLICATE: 409,
  CARD_LINE_BOOKED: 409,
  CARD_LINE_CLAIMED: 409,
  CARD_LINE_TOO_LARGE: 409,
  CARD_PAYMENT_MISSING: 404,
  CARD_PAYMENT_NOT_CREDITED: 409,
  CARD_PAYMENT_TEST: 409,
  CARD_PAYMENT_SETTLED: 409,
  CARD_CURRENCY_MISMATCH: 409,
  NOT_READY: 409,
  NOT_PENDING: 409,
  EXPIRED: 409,
  SELF_APPROVAL: 409,
  DIGEST_MISMATCH: 409,
  ALREADY: 409,
  TOO_MANY: 409,
  MFA_STALE: 403,
  UNKNOWN_PERMISSION: 400,
  STAFF_EXISTS: 409,
  EMAIL_IN_USE: 409,
  ALREADY_LINKED: 409,
  NOT_YOURSELF: 409,
  SUPER_ADMIN: 409,
  LOGIN_FAILED: 502,
  PROVIDER_REFUSED: 502,
  RATE_LIMITED: 429,
  INTERNAL: 500,
  MISCONFIGURED: 500,
  // Countries (the headquarters link, docs/workflows/market-cell.md).
  HOME_ONLY: 403,
  HQ_DISABLED: 403,
  HQ_BAD_SIGNATURE: 401,
  HQ_WRONG_CELL: 403,
  HQ_STALE: 400,
  HQ_REPLAY: 409,
  HQ_NOT_SET: 409,
  HQ_UNREACHABLE: 502,
  HQ_REFUSED: 502,
  /** The country has already applied a newer market switch than this one. */
  HQ_OUTDATED: 409,
  /** A country was taken off the list but its market switch could not be set off. */
  MONEY_STILL_ON: 502,
  /** Wallet Staging has no country switch: every country is always on. */
  STAGING_LOCKED: 409,
} as const;

export type AdminErrorCode = keyof typeof ERROR_STATUS;

export interface AdminError {
  code: AdminErrorCode | (string & {});
  message: string;
}

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/** A person's standing in another country, as that country reports it. */
export type CountryStatus =
  | 'none'
  | 'invited'
  | 'active'
  | 'disabled'
  /** The country could not be asked (down, or it refused the link). */
  | 'unreachable'
  /** Headquarters has no secret for this country yet. */
  | 'not_set_up';

export interface CountryAccess {
  code: string;
  name: string;
  status: CountryStatus;
  roles: Role[];
  /** Why the status could not be read. */
  message?: string;
}

export interface CellsAccess {
  staff: {
    id: string;
    email: string;
    display_name: string;
    disabled: boolean;
  };
  countries: CountryAccess[];
}

export interface CellsInviteAnswer {
  outcome: 'sent';
  country: string;
  status: CountryStatus;
  roles: Role[];
  /** Whether the country sent the set-password email. */
  invite_sent: boolean;
}

/** What is still missing in a country; shown as a warning, never a block. */
export type CountryWarning = 'no_providers' | 'tiers_not_open' | 'no_staff';

/** Who last changed a country's listing, and why. */
export interface CountryChange {
  /** Whether that change listed it (true) or unlisted it. */
  on: boolean;
  at: string;
  by: string | null;
  reason: string | null;
}

export interface CountryRow {
  code: string;
  name: string;
  dial_code: string;
  currency: string | null;
  /** Headquarters: always on, never switched. */
  home: boolean;
  /** Listed in the app's directory and the portal picker. */
  on: boolean;
  /** The switch cannot be used: the home country, or Wallet Staging. */
  locked: boolean;
  /** The country's own market switch, as the country says. */
  money: 'on' | 'off' | 'unknown';
  /** Why the money switch is unknown (the country could not be asked). */
  money_message?: string;
  /** Only the warnings the country's answer could support. */
  warnings: CountryWarning[];
  last_change: CountryChange | null;
}

export interface CountriesList {
  /** Wallet Staging: every country is on and the switches are locked. */
  staging: boolean;
  countries: CountryRow[];
}

/** The countries the portal picker may show this person (never Ghana itself). */
export interface CountriesOpen {
  countries: { code: string; name: string; on: boolean }[];
  /** Whether this person is shown the countries that are off (a super admin). */
  sees_off: boolean;
}

export interface CountrySwitchAnswer {
  country: string;
  on: boolean;
  /** Whether the country is in the directory now. */
  listed: boolean;
  /** The country's market switch after the request. */
  money: 'on' | 'off';
}

export interface PermissionOverride {
  permission: Permission;
  effect: 'grant' | 'revoke';
}

export interface StaffMember {
  id: string;
  email: string;
  display_name: string;
  is_super_admin: boolean;
  has_login: boolean;
  /** Their own Sao app account is linked, for alerts and checker pushes. */
  app_linked?: boolean;
  on_alert_list?: boolean;
  created_at: string;
  disabled_at: string | null;
  disabled_reason: string | null;
  last_sign_in_at: string | null;
  roles: Role[];
  overrides: PermissionOverride[];
  permissions: Permission[];
}

/** What `session.me` returns: the staff member plus the session limits. */
export interface SessionMe extends StaffMember {
  session: {
    /** Minutes idle before sign-out; null for a wall display. */
    idle_minutes: number | null;
    max_hours: number;
  };
  /**
   * The market this cell serves: money and time in its own currency and zone,
   * and the providers it has (so a page for a provider it lacks is hidden).
   * Missing until the session-market migration is on; the portal then reads
   * Ghana's values.
   */
  market?: SessionMarket;
}

export interface SessionMarket {
  code: string;
  name: string;
  home_currency: string;
  currencies: string[];
  dial_code: string;
  /** An IANA zone, such as Africa/Accra. */
  timezone: string;
  /**
   * Providers of this market in any status, with what each does. A provider
   * missing from the list is one the cell does not have; a paused one is
   * still the cell's. Cells before 20261028160000 list active ones only and
   * send no status.
   */
  providers: { code: string; status?: string; capabilities: string[] }[];
}

export interface RoleInfo {
  code: Role;
  name: string;
  description: string;
  permissions: Permission[];
}

export interface PermissionInfo {
  key: Permission;
  area: string;
  description: string;
}

export interface AuditEntry {
  id: number;
  at: string;
  staff_id: string | null;
  staff_name: string | null;
  action: string;
  outcome: 'ok' | 'refused' | 'error';
  customer_id: string | null;
  reason: string | null;
  detail: Record<string, unknown>;
}

export interface AuditFilters {
  staff_id?: string;
  customer_id?: string;
  action?: string;
  /** ISO 8601, inclusive. */
  from?: string;
  /** ISO 8601, exclusive. */
  to?: string;
  before_id?: number;
  limit?: number;
  /** Leave out successful page loads and lookups, which only read. */
  hide_reads?: boolean;
}

// ---------------------------------------------------------------------------
// Step 2: customers, issues, money screens, exports
// ---------------------------------------------------------------------------

/** A customer as every list shows them: masked. */
export interface CustomerSummary {
  id: string;
  /** 024•••••67 */
  phone: string | null;
  /** First name and initial. */
  name: string | null;
  joined_at: string;
  registration_step: string;
  verified: boolean;
  archived_at: string | null;
  limit_group: string;
  /** BCP 47 tag of the language the customer reads Sao in (`en`). */
  language: string;
  wallets: number;
}

/**
 * Where an unfinished registration stopped and why, built from the account,
 * the Didit sessions and the auth events. Null once registration completed.
 */
export interface RegistrationStatus {
  step: 'started' | 'kyc_verified' | 'identity_submitted';
  code:
    | 'no_kyc'
    | 'kyc_not_begun'
    | 'kyc_left'
    | 'kyc_expired'
    | 'kyc_in_review'
    | 'kyc_declined'
    | 'kyc_passed_not_recorded'
    | 'identity_missing'
    | 'pin_missing';
  /** failed: something went wrong; waiting: someone else is on it; stalled: the customer stopped. */
  tone: 'failed' | 'waiting' | 'stalled';
  reason: string;
  kyc_status: string | null;
  /** Whose check the latest session is; null before any. */
  kyc_provider: 'didit' | 'margins' | null;
  kyc_sessions: number;
  verification: string | null;
  error_code: string | null;
  restarts: number;
  /** Passed the ID check, then chose Start over. Missing on older servers. */
  passed_before_restart?: boolean;
  last_event: string | null;
  last_event_at: string | null;
}

/** One row of the customer directory: a customer and what they have open. */
export interface CustomerListRow extends CustomerSummary {
  open_reports: number;
  open_cases: number;
  /** Missing until the registration-status migration is on. */
  registration?: RegistrationStatus | null;
}

/** The directory's counts, whatever its filters; archived customers are not counted. */
export interface CustomerDirectorySummary {
  total: number;
  verified: number;
  /** Started and active in the last 14 days. */
  registering: number;
  /** Started, and silent for 14 days. */
  abandoned: number;
  /** The ID check passed but the registration is not complete. */
  finishing: number;
  /** Customers with at least one open report / case. */
  open_reports: number;
  open_cases: number;
}

/**
 * The configuration the super admin changes directly, with no second
 * approver but a fresh authenticator code: settings, flags, fees, limits, kill
 * switches back on, alert recipients and biller visibility.
 */
export const OWNER_CONFIG_KINDS = [
  'setting',
  'feature_flag',
  'fee_tariff',
  'fee_tariff_cancel',
  'limits',
  'killswitch_on',
  'alert_recipient',
  'biller_visibility',
] as const;

/**
 * What the super admin does directly, with no second approver: restrictions,
 * holds, bound phones and limit groups, and the configuration above. Money
 * changes and messages to customers are not here.
 */
export const OWNER_KINDS = [
  'restrict',
  'lift_restriction',
  'lift_hold',
  'revoke_device',
  'limit_group',
  ...OWNER_CONFIG_KINDS,
] as const;

export const CUSTOMER_STATUSES = [
  'verified',
  'unverified',
  'registering',
  'abandoned',
  'finishing',
  'archived',
] as const;
export const CUSTOMER_ATTENTION = ['open_report', 'open_case'] as const;

/** A customer's personal details, unmasked, as far as the viewer may see them. */
export interface CustomerDetails {
  /** With customers.reveal_phone. */
  phone?: string | null;
  full_name?: string | null;
  email?: string | null;
  region?: string | null;
  country_of_residence?: string | null;
  gender?: string | null;
  /** With customers.reveal_identity. */
  date_of_birth?: string | null;
  document_type?: string | null;
  issuing_country?: string | null;
  id_number?: string | null;
  id_expiry?: string | null;
}

export interface CustomerProfile extends CustomerSummary {
  /** The market the account belongs to; missing until the new-markets migration is on. */
  market?: string;
  /** The tier the account's evidence supports (1 to 3), null until verified. */
  verification_tier?: number | null;
  verification_tier_name?: string | null;
  registration_completed_at: string | null;
  verified_at: string | null;
  kyc_attempt: number;
  discoverable_by_phone: boolean;
  money_out_hold: {
    reason: 'pin_reset' | 'new_device' | 'emergency' | null;
    started_at: string | null;
    until: string;
    active: boolean;
  } | null;
  pin: {
    set_at: string | null;
    locked_until: string | null;
    locked: boolean;
    failed_attempts: number;
    lock_count: number;
    reset_required: boolean;
  } | null;
  archive: { archived_at: string; restored_at: string | null } | null;
  sign_in_limits: { key: string; count: number; window_start: string }[];
  /** Missing until the registration-status migration is on. */
  registration?: RegistrationStatus | null;
}

export const CUSTOMER_TABS = [
  'overview',
  'verification',
  'wallets',
  'transactions',
  'activity',
  'devices',
  'saved',
  'messages',
  'issues',
  /** The staff audit trail about this customer; for staff who read the whole log. */
  'audit',
] as const;

export type CustomerTab = (typeof CUSTOMER_TABS)[number];

/** The customer at a glance: the `overview` tab. Everything is masked. */
export interface CustomerOverview {
  wallets: {
    id: string;
    name: string;
    kind: string;
    currency: string;
    status: string;
    posted_minor: number;
    held_minor: number;
    /** Whether the customer owns the wallet. Missing on an older server: read as owned. */
    owner?: boolean;
  }[];
  /** Posted balance by currency, in minor units, across every wallet the customer is linked to. */
  balances: Record<string, number>;
  transactions: { last_30_days: number; recent: Row[] };
  issues: { open: number; total: number; recent: Row[] };
  cases: { open: number; total: number };
  devices: { count: number };
  last_activity_at: string | null;
  /** How often staff looked at this customer; null unless the viewer reads the whole audit log. */
  staff_looks_30_days: number | null;
}

/** One row of a list the portal lays out by column. Values are masked. */
export type Row = Record<string, unknown>;

export interface TransactionRow extends Row {
  id: string;
  kind: string;
  /** Where it went or came from: "SAO to MTN", "Bill: ECG Prepaid", "SAO to SAO". Missing until the channel migration is on. */
  channel?: string;
  /** The ledger kind, except a bill payment (stored as a payout) reads `bill_payment`. */
  type?: string;
  /** Who actually carried it, from its own record: hubtel, gip, access-bank, sao-core... Empty until a payout is claimed for sending. */
  processor?: string | null;
  /** Sao's own reference for the payout, bill or top-up. */
  client_reference?: string | null;
  /** The provider's reference, for reconciliation. */
  provider_reference?: string | null;
  status: string;
  /** How a problem on it ended: the break opened on it, and the line that reversed it. The ledger status is never rewritten. */
  break_id?: string;
  break_kind?: string;
  break_state?: 'open' | 'resolved';
  break_opened_at?: string;
  break_resolved_at?: string;
  break_resolved_by?: string;
  break_reason?: string;
  /** The posted line that returned or reversed this one. */
  reversed_by?: string;
  currency: string;
  amount_minor: number;
  fee_minor: number;
  created_at: string;
}

/** One row of the live transactions feed: the transaction and its customer, masked. */
export interface LiveTransaction extends TransactionRow {
  failure_code: string | null;
  customer_id: string | null;
  customer_name: string | null;
  customer_phone: string | null;
}

/** The whole platform's last hour, whatever the feed's filters. */
export interface TransactionSummary {
  window_minutes: number;
  total: number;
  posted: number;
  failed: number;
  /** Every transaction still open, however old. */
  waiting: number;
  /** Posted volume in the window, by currency, in minor units. */
  volume_minor: Record<string, number>;
}

/**
 * One thing that happened to a transaction. `by` is a rule ("Hubtel never
 * received it", "Pool check") or maker → checker; `automatic` says a rule
 * did it; `transaction_id` is a row to open (a return, a reversal, a
 * correction).
 */
export interface TransactionEvent {
  at: string;
  event: string;
  by?: string;
  automatic?: boolean;
  detail?: unknown;
  transaction_id?: string;
}

/** A refund or correction that names the transaction it fixes. */
export interface TransactionCorrection extends Row {
  id: string;
  at: string;
  status: string;
  direction: 'credit' | 'debit';
  currency: string;
  amount_minor: number;
  /** own_funds or write_offs, as finance picked it. */
  source: string | null;
  by: string;
  reason: string;
  wallet_number: string | null;
}

export interface TransactionDetail extends TransactionRow {
  customer: CustomerSummary | null;
  entries: Row[];
  fees: Row[];
  approvals: Row[];
  legs: Record<string, Row>;
  timeline: TransactionEvent[];
  /** On an adjustment: the transaction it fixes. */
  corrects?: string | null;
  /** Adjustments that name this transaction. Missing until the corrections migration is on. */
  corrections?: TransactionCorrection[];
}

export type IssueStatus =
  | 'new'
  | 'in_progress'
  | 'waiting_customer'
  | 'waiting_provider'
  | 'resolved'
  | 'closed';

export const ISSUE_STATUSES: readonly IssueStatus[] = [
  'new',
  'in_progress',
  'waiting_customer',
  'waiting_provider',
  'resolved',
  'closed',
];

export interface IssueRow {
  id: string;
  /** The short number an agent says aloud ("#1042"); search for it with `q`. */
  ref_no: number;
  customer: CustomerSummary | null;
  note: string;
  status: IssueStatus;
  product: 'send' | 'add_money' | 'momo_payout' | 'bill' | null;
  amount_minor: number | null;
  transaction_id: string | null;
  owner: { id: string; name: string } | null;
  created_at: string;
  updated_at: string;
  due_at: string;
  resolved_at: string | null;
  overdue: boolean;
  due_soon: boolean;
}

export interface IssueDetail extends IssueRow {
  context: Row;
  device: Row;
  logs: unknown[];
  events: {
    id: number;
    at: string;
    staff: string;
    kind: 'status' | 'owner' | 'note' | 'file';
    from: string | null;
    to: string | null;
    note: string | null;
  }[];
  files: {
    id: string;
    name: string;
    content_type: string;
    size_bytes: number;
    added_by: string;
    added_at: string;
  }[];
}

export interface IssueFilters {
  view?: 'open' | 'overdue' | 'due_soon' | 'mine' | 'all';
  status?: IssueStatus;
  /** A staff id, or `none` for unassigned. */
  owner?: string;
  product?: 'send' | 'add_money' | 'momo_payout' | 'bill';
  older_than_hours?: number;
  customer_id?: string;
  /** An issue number ("1042" or "#1042") or the start of an id. */
  q?: string;
  /** When the report was raised: a timestamp, or a UTC day (YYYY-MM-DD). Both ends included. */
  from?: string;
  to?: string;
  before?: string;
  limit?: number;
}

export interface ExportRow {
  id: string;
  source: AdminAction;
  title: string;
  filters: Row;
  row_count: number;
  truncated: boolean;
  status: 'building' | 'ready' | 'failed' | 'deleted';
  size_bytes: number | null;
  error: string | null;
  created_at: string;
  ready_at: string | null;
}

/** Money screens answer a page of rows, sometimes with totals. */
export interface RowsPage {
  rows: Row[];
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Sweeps: Hubtel's money to the Access pool (decision 0008, phase 3)
// ---------------------------------------------------------------------------

export type SweepStatus =
  | 'requested'
  | 'approved'
  | 'moving'
  | 'sent'
  | 'paid'
  | 'booked'
  | 'failed'
  | 'unknown'
  | 'kept';

/** One sweep, with a time per status. The Access statement line is the match, without any running balance. */
export interface SweepRow {
  id: string;
  sao_reference: string;
  amount_minor: number;
  currency: string;
  from_gl: string;
  to_gl: string;
  status: SweepStatus;
  transfer_status: string | null;
  hubtel_status: string | null;
  hubtel_refs: Record<string, unknown>;
  requested_by: string | null;
  approved_by: string | null;
  requested_at: string;
  approved_at: string | null;
  moving_at: string | null;
  moved_at: string | null;
  sent_at: string | null;
  paid_at: string | null;
  booked_at: string | null;
  failed_at: string | null;
  unknown_at: string | null;
  last_checked_at: string | null;
  note: string | null;
  break_id: string | null;
  transaction_id: string | null;
  /** The send was refused or not taken after the transfer: the money waits in the disbursement account for a resend. */
  send_held: boolean;
  /** Which Send-To-Bank this is (0 is the first; a resend is 1, 2...). */
  attempt: number;
  /** Send-To-Bank's fee, charged on top of the amount, once Hubtel has said paid. */
  charge_minor: number | null;
  /** A person attached the Access credit that did not carry the reference. */
  matched: boolean;
  /** Unexplained Access credits for this sweep's amount a person could attach (sent, paid or unknown only). */
  match_candidates: {
    line_id: string;
    reference: string | null;
    narration: string | null;
    value_date: string | null;
  }[];
  statement: {
    line_id: string;
    reference: string | null;
    narration: string | null;
    value_date: string | null;
    amount_minor: number;
  } | null;
  /** Whether this person may approve it (another person asked for it). */
  can_approve: boolean;
  can_cancel: boolean;
  can_resend: boolean;
  can_match: boolean;
  can_close: boolean;
}

export type SweepRequestBlock =
  | 'switch_off'
  | 'sweep_open'
  | 'cover_failed'
  | 'nothing_owed';

/** A check's last result. No balance of any account is ever in it. */
export interface SweepCheck {
  result: 'ok' | 'mismatch' | 'unreadable' | 'none';
  checked_at?: string;
  fresh?: boolean;
  business_date?: string;
}

export type SweepDayState = 'quiet' | 'today' | 'needs_signoff' | 'signed';

/** One day of the sweeps audit report (Accra days; never a bank balance). */
export interface SweepDailyRow {
  day: string;
  requested: number;
  booked: number;
  booked_minor: number;
  failed: number;
  unknown: number;
  held: number;
  open: number;
  fees_minor: number;
  /** Booked amounts less the statement credits they matched; zero when the books agree. */
  unmatched_minor: number;
  cover_checks: number;
  cover_failed: number;
  /** What Hubtel holds and has in transit less what it owes the pool; not negative. */
  cover_last_minor: number | null;
  pool_checks: number;
  pool_failed: number;
  breaks_opened: number;
  difference_ok: boolean;
  state: SweepDayState;
  signed_by: string | null;
  signed_at: string | null;
  signoff_note: string | null;
}

export interface SweepDailyReport {
  rows: SweepDailyRow[];
  currency: string;
  today: string;
}

export interface SweepAutoEvent {
  at: string;
  kind: string;
  policy_version: number | null;
  detail: Record<string, unknown>;
}

export interface SweepAutoState {
  /** The `sweeps.auto` setting. */
  setting: 'on' | 'off';
  /** The `sweeps.switch` setting: automatic sweeps need both. */
  switch: 'on' | 'off';
  /** Both on and not paused. */
  running: boolean;
  paused: boolean;
  paused_at: string | null;
  paused_trigger: string | null;
  paused_detail: Record<string, unknown> | null;
  resumed_at: string | null;
  resumed_by: string | null;
  last_cycle_at: string | null;
  last_cycle_result: string | null;
  preflight_failures: number;
  policy_version: number;
  today: { count: number; minor: number; canary_booked: boolean };
  events: SweepAutoEvent[];
  can_resume: boolean;
}

export interface SweepPolicy {
  version: number;
  float_low_minor: number;
  float_target_minor: number;
  bills_reserve_minor: number;
  min_sweep_minor: number;
  max_sweep_minor: number;
  max_day_minor: number;
  max_sweeps_per_day: number;
  window_start_hour: number;
  window_end_hour: number;
  canary_minor: number;
  expected_fee_bps: number;
  /** A fee up to this never pauses, whatever the percentage (Send-To-Bank has a minimum charge). */
  fee_floor_minor: number;
  reason: string | null;
  proposed_by: string | null;
  approved_by: string | null;
  created_at: string;
}

/** Newest first: the first is the policy in force. */
export interface SweepPolicyPage {
  gl: string;
  currency: string;
  versions: SweepPolicy[];
}

export type SweepExceptionKind =
  | 'held_send'
  | 'unknown_sweep'
  | 'slow_sweep'
  | 'unexplained_debit'
  | 'failed_sweep';

export interface SweepException {
  kind: SweepExceptionKind;
  reference: string;
  amount_minor: number | null;
  since: string;
  age_minutes: number;
  reason: string;
  next_step: string;
}

export interface SweepLedgerRow {
  sao_reference: string;
  status: SweepStatus;
  amount_minor: number;
  currency: string;
  from: string;
  to: string;
  requested_by: string | null;
  approved_by: string | null;
  requested_at: string;
  approved_at: string | null;
  moved_at: string | null;
  sent_at: string | null;
  paid_at: string | null;
  booked_at: string | null;
  failed_at: string | null;
  unknown_at: string | null;
  send_attempts: number;
  send_held: boolean;
  fee_minor: number | null;
  transfer_ref: string | null;
  send_code: string | null;
  statement_reference: string | null;
  statement_amount_minor: number | null;
  amounts_agree: boolean | null;
  note: string | null;
}

/** Money > Sweeps. The Access pool's cash is only above or below the floor, never a figure. */
export interface SweepsOverview {
  switch: 'on' | 'off';
  gl: { code: string; name: string; status: string } | null;
  currency: string;
  /** What Hubtel owes the pool now. */
  owed_minor: number;
  /** When the oldest of it was credited; null when nothing is owed. */
  owed_since: string | null;
  owed_age_limit_hours: number;
  bills_reserve_minor: number;
  /** What a sweep is prepared for: what is owed less the bills reserve. */
  prepared_minor: number;
  checks: { hubtel_collection: SweepCheck; access_pool: SweepCheck };
  floor: {
    status: 'above' | 'below' | 'unknown' | 'not_set';
    checked_at: string | null;
  };
  blackout: boolean;
  open_sweep_id: string | null;
  request_blocked: SweepRequestBlock | null;
  can_request: boolean;
  rows: SweepRow[];
  next_before: string | null;
}

// ---------------------------------------------------------------------------
// Step 3: change requests, one-person stops, alerts
// ---------------------------------------------------------------------------

export const CHANGE_KINDS = [
  'resolve_stuck',
  'resolve_collection',
  'resolve_payout',
  'resolve_bill',
  'payout_return',
  'bill_return',
  'release_suspense',
  'adjustment',
  'reversal',
  'pool_booking',
  'resolve_break',
  'restrict',
  'lift_restriction',
  'revoke_device',
  'lift_hold',
  'limit_group',
  'killswitch_on',
  'alert_recipient',
  'confirm_freeze',
  'fee_tariff',
  'fee_tariff_cancel',
  'limits',
  'feature_flag',
  'setting',
  'biller_visibility',
  'sweep_policy',
  'notice_publish',
  'message_segment',
  'referral_reward',
  'statement_credit',
  'deposit_credit',
  'card_settlement',
  'market_on',
  'kyc_tier',
] as const;

export type ChangeKind = (typeof CHANGE_KINDS)[number];

/**
 * Who may propose each kind (any one permission), mirroring
 * `admin.change_kinds`. The portal hides what a staff member cannot
 * propose; the database decides.
 */
export const CHANGE_KIND_PROPOSE: Record<ChangeKind, readonly Permission[]> = {
  resolve_stuck: ['money_ops.propose', 'adjustments.propose'],
  resolve_collection: ['money_ops.propose', 'adjustments.propose'],
  resolve_payout: ['money_ops.propose', 'adjustments.propose'],
  resolve_bill: ['money_ops.propose', 'adjustments.propose'],
  payout_return: ['money_ops.propose'],
  bill_return: ['money_ops.propose'],
  release_suspense: ['money_ops.propose'],
  adjustment: ['adjustments.propose'],
  reversal: ['reversals.propose'],
  pool_booking: ['money_ops.propose'],
  resolve_break: ['money_ops.propose'],
  restrict: ['restrictions.propose'],
  lift_restriction: ['restrictions.propose'],
  revoke_device: ['restrictions.propose'],
  lift_hold: ['restrictions.propose'],
  limit_group: ['limits.propose'],
  killswitch_on: ['killswitch.propose'],
  alert_recipient: ['settings.propose'],
  // Created by a one-person freeze, never proposed.
  confirm_freeze: [],
  fee_tariff: ['fees.propose'],
  fee_tariff_cancel: ['fees.propose'],
  limits: ['limits.propose'],
  feature_flag: ['settings.propose'],
  setting: ['settings.propose'],
  biller_visibility: ['settings.propose'],
  sweep_policy: ['money_ops.propose'],
  notice_publish: ['settings.propose'],
  message_segment: ['settings.propose'],
  referral_reward: ['referral_rewards.propose'],
  statement_credit: ['credits.propose'],
  deposit_credit: ['credits.propose'],
  card_settlement: ['money_ops.propose'],
  market_on: ['settings.propose'],
  kyc_tier: ['limits.propose'],
};

export type ChangeStatus =
  | 'pending'
  | 'applied'
  | 'rejected'
  | 'withdrawn'
  | 'expired'
  | 'failed';

/** One unexplained credit on the pool's statement (credits.lines). */
export interface CreditLine {
  id: string;
  amount_minor: number;
  currency: string;
  posted_at: string | null;
  value_date: string | null;
  reference: string | null;
  narration: string | null;
  /** `claimed`: a credit request waits on it. `used`: a credit booked it. */
  status: 'available' | 'claimed' | 'used';
}

/** One card payment (money.cards). Never more of the card than its brand and last four digits. */
export interface CardPayment {
  id: string;
  created_at: string;
  reference: string;
  customer_id: string;
  wallet_id: string;
  amount_minor: number;
  currency: string;
  card_scheme: string | null;
  card_last4: string | null;
  status:
    | 'requested'
    | 'pending'
    | 'credited'
    | 'failed'
    | 'not_completed'
    | 'review';
  /** SaoPay's own word for the payment's state. */
  core_status: string | null;
  core_environment: 'TEST' | 'LIVE' | null;
  credited_at: string | null;
  /** Whether the bank has settled it; null when it was never credited. */
  settlement: 'settled' | 'not_settled' | null;
  settled_at: string | null;
  /** The reference of the pool credit that settled it. */
  statement_reference: string | null;
  transaction_id: string | null;
  core_order_id: string | null;
  core_session_id: string | null;
  core_message: string | null;
}

export interface CardPaymentsSummary {
  /** Credited LIVE payments the bank has not settled yet, all time. */
  not_settled: {
    minor: number;
    count: number;
    oldest_credited_at: string | null;
  };
  settled_in_period: { minor: number; count: number };
  /** Failed and not completed payments started in the period. */
  failed_in_period: number;
  /** Not credited, and SaoPay says MANUAL_RECONCILIATION or INDETERMINATE_TSQ_PENDING. */
  waiting_on_core: { count: number; minor: number };
}

/** A pool credit that could be a card settlement (money.card_settlement_lines). */
export interface CardSettlementLine {
  id: string;
  amount_minor: number;
  currency: string;
  posted_at: string | null;
  value_date: string | null;
  reference: string | null;
  narration: string | null;
  class: 'unexplained' | 'card_settlement';
  /** `claimed`: a pending request names it. */
  state: 'available' | 'claimed';
}

/** A payment a settlement suggestion lists (money.card_settlement_suggest). */
export interface CardSettlementSuggestedPayment {
  id: string;
  reference: string;
  created_at: string;
  credited_at: string;
  customer_id: string;
  amount_minor: number;
  currency: string;
  card_scheme: string | null;
  card_last4: string | null;
}

/**
 * The payload of a `card_settlement` change request. Amounts are read from the
 * database, never from here. A credit for less than the payments books the
 * difference as a card charge out of the pool; for more, it is refused.
 */
export interface CardSettlementPayload {
  line_id: string;
  /** 1 to 500 distinct card payment ids. */
  collection_ids: string[];
  /** 8 to 500 characters. */
  reason: string;
}

export interface ChangeEvidence {
  type:
    | 'transaction'
    | 'issue'
    | 'case'
    | 'statement_line'
    | 'break'
    | 'file'
    | 'note';
  ref: string;
}

export interface ChangeRequest {
  id: string;
  kind: ChangeKind;
  title: string;
  summary: string;
  payload: Row;
  /** SHA-256 of the kind and payload; the checker approves this digest. */
  digest: string;
  /** The target when the request was proposed. */
  current: Row | null;
  amount_minor: number | null;
  currency: string | null;
  customer: CustomerSummary | null;
  maker: { id: string; name: string };
  reason: string;
  evidence: ChangeEvidence[];
  checkers_needed: number;
  approvals: {
    staff: string;
    staff_id: string;
    decision: 'approve' | 'reject';
    comment: string | null;
    source: 'own_funds' | 'write_offs' | null;
    at: string;
  }[];
  status: ChangeStatus;
  expires_at: string;
  created_at: string;
  decided_at: string | null;
  comment: string | null;
  applied_at: string | null;
  /** The existing function's answer: `ok`, or `pending` for a core movement. */
  result: string | null;
  failure: string | null;
  operator_action_id: string | null;
}

export interface ChangeDetail extends ChangeRequest {
  /** The target now, or why it can no longer be read. */
  now: Row | { error: AdminError } | null;
  can_approve: boolean;
  can_withdraw: boolean;
  needs_source: boolean;
}

export const KILL_SWITCHES = [
  'transfers',
  'funding',
  'payouts',
  'bank_payouts',
  'bills',
  'cards',
] as const;

export type KillSwitch = (typeof KILL_SWITCHES)[number];

/** A switch is on only when its secret and its setting both say so. */
export interface SwitchState {
  secret: boolean;
  setting: boolean;
  on: boolean;
}

/**
 * Hubtel's disbursement balance on the status board, asked live through the
 * whitelisted egress: `unknown` when Hubtel gave no answer in time, `off`
 * when Hubtel is not set up.
 */
export type HubtelFloat =
  | { state: 'ok'; amount_minor: number; currency: 'GHS' }
  | { state: 'unknown' }
  | { state: 'off' };

export const ALERT_CLASSES = [
  'breaks',
  'pool',
  'issues',
  'bills_float',
  'stops',
  'change_requests',
  'kyc_reviews',
] as const;

export type AlertClass = (typeof ALERT_CLASSES)[number];

/** A staff member's ops alerts. Email goes to their staff address. */
export interface AlertChoices {
  linked: boolean;
  listed: boolean;
  alerts: AlertClass[];
  sms_alerts: AlertClass[];
  email_alerts: AlertClass[];
  email: string;
}

// ---------------------------------------------------------------------------
// Step 4: configuration, communications, cases, schedules
// ---------------------------------------------------------------------------

export const FEE_PRODUCTS = [
  'sao_transfer',
  'group_in',
  'group_out',
  'momo_payout',
  'bank_payout',
  'bill_payment',
  'add_money',
] as const;

export type FeeProduct = (typeof FEE_PRODUCTS)[number];

/** One rule of a tariff, as set_fee_tariff takes it (minor units). */
export interface FeeRule {
  component: 'sao' | 'provider';
  rail?: string | null;
  from?: number;
  to?: number | null;
  flat?: number;
  bps?: number;
  min?: number;
  max?: number | null;
}

export interface FeeTariff {
  id: string;
  product: FeeProduct;
  currency: string;
  effective_from: string;
  note: string | null;
  cancelled_at: string | null;
  created_at: string;
  state: 'scheduled' | 'in_force' | 'replaced' | 'cancelled';
  approved_by: string | null;
  rules: FeeRule[];
}

export interface FeePrice {
  sao_minor: number;
  provider_minor: number;
  total_minor: number;
}

export interface LimitField {
  scope: 'group' | 'currency';
  field: string;
  min: number;
  max: number | null;
  label: string;
}

export interface LimitGroup extends Row {
  code: string;
  name: string;
  is_default: boolean;
  customers: number;
  currencies: Row[];
}

export interface FeatureFlag {
  key: string;
  description: string;
  environments: Record<string, { enabled: boolean; updated_at: string }>;
}

/** A market this cell serves (public.markets, the market registry migration). */
export interface MarketRow {
  code: string;
  name: string;
  home_currency: string;
  currencies: string[];
  dial_code: string;
  timezone: string;
  enabled: boolean;
  /** The per-market kill switch (killswitch.market): false stops every money surface here. */
  switch: boolean;
}

/** The status of a KYC tier (public.kyc_tiers): `proposed` figures are kept for review and read as nothing. */
export type KycTierStatus = 'open' | 'sandbox' | 'proposed' | 'blocked';

/** One tier of the market this cell serves (kyc_tiers.list). Amounts are minor units of `limit_currency`; null is "none set". */
export interface KycTierRow {
  market: string;
  tier: number;
  name: string;
  status: KycTierStatus;
  valid_until: string | null;
  /** Open, or sandbox and not yet past its expiry. */
  in_force: boolean;
  limit_currency: string;
  max_balance_minor: number | null;
  per_transaction_limit_minor: number | null;
  daily_limit_minor: number | null;
  monthly_limit_minor: number | null;
  source: string;
  note: string | null;
  updated_at: string;
  /** Customers of this market at this verification tier. */
  customers: number;
  /** A waiting `kyc_tier` change request for this tier, if any. */
  change_request_id: string | null;
}

/** A provider a market works with: one adapter each (public.providers). */
export interface ProviderRow {
  code: string;
  market: string;
  name: string;
  capabilities: string[];
  /** The plugin folder under supabase/functions/_plugins. */
  plugin: string;
  status: 'active' | 'paused' | 'retired';
}

/** A settlement account a provider holds for Sao, by role: never a number or a balance. */
export interface ProviderAccountRow {
  provider: string;
  market: string;
  gl: string;
  currency: string;
  name: string;
  kind: string;
  status: string;
  is_default: boolean;
}

/** A route of the registry (public.market_route_registry): `chosen` marks the one its channel uses now. */
export interface MarketRouteRow {
  market: string;
  channel: 'momo' | 'bank' | 'bills';
  direction: 'in' | 'out';
  /** The mobile money network, or '*' for a channel with none. */
  network: string;
  code: string;
  provider: string;
  plugin: string;
  rail: string;
  gl: string | null;
  currency: string | null;
  setting_key: string | null;
  is_default: boolean;
  enabled: boolean;
  provider_status: ProviderRow['status'];
  gl_status: string | null;
  chosen: boolean;
}

/** What the registry says, read only (markets.registry). */
export interface MarketRegistry {
  /** The market this cell serves (markets.home). */
  home: string;
  markets: MarketRow[];
  providers: ProviderRow[];
  accounts: ProviderAccountRow[];
  routes: MarketRouteRow[];
}

/** One partner of the partner API (docs/features/partner-api.md), with no secret in it. */
export interface PartnerCredential {
  id: string;
  kind: 'client_secret' | 'api_key' | 'hmac_secret' | 'webhook_secret';
  /** The client id or API key id; null for the two signing secrets. */
  identifier: string | null;
  /** What staff call it ("Pool credit service"), so sets can be told apart. */
  name: string | null;
  created_at: string;
  /** When partner-api last saw it used (noted at most every five minutes). */
  last_used_at: string | null;
  /** Set while it is being replaced: it stops working then. */
  expires_at: string | null;
  revoked_at: string | null;
}

/** A wallet a partner pays from in a Sao-to-Sao transfer. */
export interface PartnerWallet {
  wallet_id: string;
  wallet_number: string;
  name?: string;
  status?: string;
  currency: string;
  /** Pesewas the partner can send now: posted less held. */
  balance_minor: number;
  held_minor?: number;
}

export const PARTNER_PRODUCTS = [
  'name_enquiry',
  'cashin',
  'cashout',
  'balance',
  'pool_credit',
  'wallet_transfer',
] as const;
export type PartnerProduct = (typeof PARTNER_PRODUCTS)[number];

export interface Partner {
  id: string;
  code: string;
  name: string;
  status: 'sandbox' | 'live' | 'suspended';
  products: PartnerProduct[];
  webhook_url: string | null;
  ip_allowlist: string[];
  mtls_fingerprint: string | null;
  name_enquiry_daily_cap: number;
  rate_per_second: number;
  suspended_at: string | null;
  suspended_reason: string | null;
  created_at: string;
  channels: { code: string; enabled: boolean }[];
  credentials: PartnerCredential[];
  calls_24h: number;
  last_call_at: string | null;
}

/** What a credential's plain value is, shown to the staff member once. */
export type CertificationCase =
  | 'token_issued'
  | 'wrong_secret_refused'
  | 'bad_signature_refused'
  | 'name_enquiry_found'
  | 'wallet_not_found'
  | 'credit_lands'
  | 'same_call_replays'
  | 'reference_reused'
  | 'found_by_reference';

/** A sandbox partner's certification: all nine cases seen means it passed. */
export interface PartnerCertification {
  /** All nine cases; `required` says whether this partner's products ask for it. */
  cases: {
    key: CertificationCase;
    seen_at: string | null;
    required: boolean;
  }[];
  passed: boolean;
}

export interface PartnerActivity {
  at: string;
  endpoint:
    | 'name-enquiry'
    | 'cashin'
    | 'cashout'
    | 'balance-enquiry'
    | 'reversal';
  reference: string;
  status: number | null;
  /** The answer's status word or error code, or PENDING while it runs. */
  result: string | null;
  latency_ms: number | null;
}

export interface IssuedCredential {
  id: string;
  kind: PartnerCredential['kind'];
  identifier: string | null;
  /** The value to hand to the partner. An API key reads `<id>.<secret>`. Never stored in plain. */
  secret: string;
}

export interface SettingRow {
  key: string;
  kind: 'boolean' | 'integer' | 'text' | 'text_list';
  description: string;
  min: number | null;
  max: number | null;
  allowed: string[] | null;
  /** The value one person may switch to without a code (a route's `off`), or null. */
  stop: string | null;
  value: unknown;
  updated_at: string | null;
  updated_by: string | null;
  history: {
    old: unknown;
    new: unknown;
    at: string;
    by: string | null;
    change_request_id: string | null;
  }[];
}

export interface Biller {
  id: string;
  name: string;
  category: string;
  hidden: boolean;
  sort: number;
  updated_at: string;
}

export interface ServiceNotice {
  id: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  body: string;
  action_label: string | null;
  action_route: string | null;
  dismissible: boolean;
  starts_at: string;
  ends_at: string | null;
  published_by: string;
  archived_at: string | null;
  state: 'scheduled' | 'live' | 'ended' | 'archived';
}

export interface MessageCampaign {
  /** The change request that sent it. */
  id: string;
  template: string;
  title: string | null;
  segment: string;
  applied_at: string;
  sent: number;
  waiting: number;
  cancelled: number;
}

export interface MessageTemplate {
  code: string;
  name: string;
  category: string;
  title: string;
  body: string;
  route: string | null;
  allow_sms: boolean;
  allow_segment: boolean;
}

/**
 * One of the server's customer messages in a language other than English
 * (public.message_catalogue): what a customer who reads that language gets
 * instead of the English in the code. `sms` is null when the body is the
 * SMS too.
 */
export interface MessageTranslation {
  code: string;
  language: string;
  title: string | null;
  body: string;
  sms: string | null;
  updated_at: string;
}

export interface Delivery {
  id: number;
  event: string;
  category: string;
  title: string;
  sms_requested: boolean;
  created_at: string;
  delivered_at: string | null;
  attempts: number;
  last_error: string | null;
  push: {
    sent_at: string;
    receipt: string | null;
    error: string | null;
    checked_at: string | null;
  }[];
  sms: {
    provider: string;
    status: string;
    detail: string | null;
    at: string;
  }[];
}

export const CASE_TYPES = [
  'debited_not_paid',
  'sent_not_received',
  'bill_not_credited',
  'wrong_recipient',
  'not_made_by_customer',
  'fee_dispute',
] as const;

export type CaseType = (typeof CASE_TYPES)[number];

export const CASE_OUTCOMES = [
  'no_fault',
  'refunded',
  'reversed',
  'recovered_from_provider',
  'written_off',
] as const;

export type CaseOutcome = (typeof CASE_OUTCOMES)[number];

export type CaseStatus =
  | 'open'
  | 'waiting_customer'
  | 'waiting_provider'
  | 'closed';

export interface CaseRow {
  id: string;
  type: CaseType;
  status: CaseStatus;
  summary: string;
  customer: CustomerSummary | null;
  owner: { id: string; name: string } | null;
  outcome: CaseOutcome | null;
  outcome_reason: string | null;
  opened_at: string;
  updated_at: string;
  closed_at: string | null;
  links: number;
  open_claims: number;
}

export interface CaseClaim {
  id: string;
  provider: string;
  reference: string;
  asked_on: string;
  status: 'asked' | 'answered' | 'recovered' | 'refused';
  answer: string | null;
}

export interface CaseDetail extends CaseRow {
  transactions: Row[];
  issues: Row[];
  claims: CaseClaim[];
  changes: Row[];
  events: {
    at: string;
    kind: string;
    detail: Row;
    note: string | null;
    staff: string | null;
  }[];
}

export interface ExportSchedule {
  id: string;
  source: string;
  title: string;
  filters: Row;
  every: 'daily' | 'weekly';
  hour_utc: number;
  weekday: number | null;
  next_run_at: string;
  last_run_at: string | null;
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

/**
 * What each action takes (`args`) and answers (`data`). `auth.request_link`
 * is the only one that runs without a session.
 */
export const KYC_DECISIONS = ['approve', 'decline'] as const;

export const REFERRAL_REWARD_STATUSES = [
  'accrued',
  'awaiting_approval',
  'approved',
  'paid',
  'void',
  'rejected',
] as const;

export type ReferralRewardStatus = (typeof REFERRAL_REWARD_STATUSES)[number];

/** One reward a referral earned, as the queue lists it. */
export interface ReferralReward {
  id: string;
  referral_id: string;
  role: 'referrer' | 'referred' | 'bonus';
  kind: 'wallet_credit' | 'points';
  /** Pesewas for a wallet credit, points for points. */
  amount_minor: number;
  currency: 'GHS' | 'PTS';
  points: number | null;
  status: ReferralRewardStatus;
  created_at: string;
  decided_at: string | null;
  paid_at: string | null;
  reason: string | null;
  /** The referral was voided after this reward was paid: staff decide. */
  needs_review: boolean;
  transaction_id: string | null;
  beneficiary: CustomerSummary | null;
  /** A change request waiting to pay it. */
  change_request_id: string | null;
}

export interface ReferralRewardsSummary {
  reward_kind: 'none' | 'wallet_credit' | 'points';
  need_approval: boolean;
  counts: Partial<Record<ReferralRewardStatus, number>>;
  month_start: string;
  /** Null: no monthly budget is set. */
  budget_minor: number | null;
  used_minor: number;
}

/** A Didit session waiting for a person. */
export const REFERRAL_STATUSES = ['pending', 'qualified', 'void'] as const;
export const REFERRAL_VOID_REASONS = [
  'expired',
  'shared_device',
  'staff',
] as const;

/** One referral, both people masked. */
export interface ReferralRow extends Row {
  id: string;
  status: (typeof REFERRAL_STATUSES)[number];
  void_reason: (typeof REFERRAL_VOID_REASONS)[number] | null;
  created_at: string;
  /** Kept when staff void a qualified referral, so the history shows it. */
  qualified_at: string | null;
  referrer_id: string;
  referrer_name: string | null;
  referrer_phone: string | null;
  referred_id: string;
  referred_name: string | null;
  referred_phone: string | null;
}

/** The programme's totals, whatever the list's filters. */
export interface ReferralSummary {
  total: number;
  pending: number;
  qualified: number;
  void: number;
  void_expired: number;
  void_shared_device: number;
  void_staff: number;
  qualified_last_30_days: number;
}

/** The counts of one payments window on a wall. Never a customer, a number or an amount. */
export interface WallWindow {
  attempts: number;
  completed: number;
  failed_system: number;
  failed_customer: number;
  /** Failures that are neither clearly the rail's nor the customer's (e.g. "Upstream transfer failed"); not in the rate. */
  failed_ambiguous?: number;
  in_flight: number;
  /** Completed of completed plus system failures (same classes as the rail outage alert); null when `too_few` or nothing finished. */
  success_pct: number | null;
  /** Fewer than 5 attempts: too few to quote a rate. */
  too_few: boolean;
  p90_seconds: number | null;
  top_failure: { code: string; count: number } | null;
}

export type WallRail =
  | 'topup'
  | 'withdraw_momo'
  | 'withdraw_bank'
  | 'bills_airtime_data'
  | 'bills_electricity'
  | 'bills_water_tv'
  | 'sao_to_sao';

export interface WallRailRow {
  rail: WallRail;
  network: string | null;
  h1: WallWindow;
  h24: WallWindow;
  /** 12 clock hours, oldest first; absent on a server that predates the hourly strip. */
  hourly_12h?: WallHour[];
}

export interface WallPayments {
  generated_at: string;
  rails: WallRailRow[];
  /** 24 buckets of 15 minutes over the last 6 h, oldest first. */
  buckets: { start: string; completed: number; failed_system: number }[];
  failures: {
    code: string;
    class: 'system' | 'customer' | 'ambiguous';
    count: number;
  }[];
}

export interface WallProviderWindow {
  attempts: number;
  ok: number;
  /** System failures only: they are what the rate counts against. */
  failed: number;
  /** Unclear failures, left out of the rate. */
  failed_ambiguous?: number;
  success_pct: number | null;
  too_few: boolean;
  p90_seconds: number | null;
}

/** One clock hour of a strip: finished attempts (or probes) and how many were ok. */
export interface WallHour {
  hour_start: string;
  /** Providers: completed plus system failures, so a strip matches the rate. */
  total: number;
  ok: number;
  /** Providers only: unclear failures that hour, not in `total`. */
  ambiguous?: number;
  /** supabase_platform only: degraded or partial readings that hour (counted in `ok`). */
  degraded?: number;
}

export type WallProviderId =
  | 'access_topups'
  | 'access_withdrawals'
  | 'access_gip'
  | 'access_name_check'
  | 'hubtel_bills'
  | 'hubtel_sms'
  | 'zavu_sms'
  | 'didit'
  | 'margins'
  | 'expo_push'
  | 'hubtel_egress';

/** `activity` providers (Didit) report what they decided; they have no failure signal. Margins (NIA) is a `rate` provider rated on whether it answered: a check with a Margins reference is ok whatever it says; a decline without one, or no answer at all, is not. */
export type WallProviderRow =
  | { id: WallProviderId; kind: 'not_connected' }
  | {
      id: WallProviderId;
      kind: 'rate' | 'activity';
      h1: WallProviderWindow;
      h24: WallProviderWindow;
      last_success_at: string | null;
      last_failure_at: string | null;
      by_network:
        | {
            network: string;
            attempts: number;
            success_pct: number | null;
          }[]
        | null;
      /** The last 15 minutes: what is happening now (absent from a server older than the migration that added it). */
      m15?: WallProviderWindow;
      /** 24 clock hours, oldest first; null for activity providers (no failure signal). */
      hourly_24h: WallHour[] | null;
      /** Share ok over 24 h; null with fewer than 5 attempts or for activity providers. */
      uptime_24h_pct: number | null;
    };

export interface WallProviders {
  generated_at: string;
  providers: WallProviderRow[];
}

export interface WallServiceRow {
  id:
    | 'api'
    | 'admin_api'
    | 'database'
    | 'signin'
    | 'egress'
    | 'supabase_platform';
  state: 'operating' | 'degraded' | 'down' | 'no_data';
  /** supabase_platform only: the affected services ("Realtime: partial outage"), or a maintenance note. */
  detail?: string | null;
  last_ok_at: string | null;
  last_fail_at: string | null;
  latency_p50_ms_15m: number | null;
  latency_p95_ms_15m: number | null;
  /** Share of ok probes since 00:00 GMT, two decimals; null with no probe today. */
  uptime_today_pct: number | null;
  checks_today: number;
  series_60m: { t: string; ok: boolean; latency_ms: number | null }[];
  /** 24 clock hours of probes, oldest first. */
  hourly_24h: WallHour[];
  /** Share of ok probes over the last 24 h, two decimals; null with no probe. */
  uptime_24h_pct: number | null;
}

export interface WallServices {
  generated_at: string;
  services: WallServiceRow[];
}

export interface WallSecurityCounts {
  pin_rejected: number;
  pin_locked: number;
  device_bound: number;
  device_bind_rejected: number;
  not_me: number;
}

export interface WallSecurity {
  generated_at: string;
  h1: WallSecurityCounts;
  h24: WallSecurityCounts;
  prompts_unanswered: number;
  money_out_holds_active: number;
  rate_limit_hits_60m: number;
}

/** One line of the contact wall's agent queue. Initials only, never a name. */
export interface WallQueueLine {
  /** 0 for Unassigned, then 1 to 5 by lateness. */
  rank: number;
  /** "AB", or "Unassigned". */
  initials: string;
  unassigned: boolean;
  open: number;
  late: number;
}

/** One of the last three transactions: a band, no id, no customer. */
export interface WallFeedLine {
  at: string;
  label: string;
  network: string | null;
  /** "GHS 10–50", "under GHS 10", "over GHS 1,000" or a currency code. */
  amount: string;
  /** "completed", "in flight", "failed: CODE", ... */
  outcome: string;
  tone: 'ok' | 'warn' | 'bad';
}

/** A rail outage, in the shape the walls read it. */
export type WallIncident = {
  kind: 'rail_outage';
  status: string;
  opened_at: string;
  resolved_at: string | null;
  detail: {
    rail: string | null;
    network: string | null;
    subject: string | null;
  };
};

/** Where a customer who has not finished registering stopped. */
export const WALL_STOP_STEPS = [
  'id_check_not_opened',
  'id_check',
  'identity_details',
  'pin',
  'system',
] as const;
export type WallStopStep = (typeof WALL_STOP_STEPS)[number];

/** Registration counts for the contact wall. Counts only: no names, phones or ids. */
export interface WallRegistrations {
  /** Completed registration since 00:00 GMT. */
  registered_today: number;
  /** Started in the last 24 h, not finished, and still moving or with the ID check in review. */
  in_progress: number;
  /** Started in the last 24 h and stopped. */
  stopped: number;
  /** Top 3 steps they stopped at, most first. */
  stopped_by_step: { step: WallStopStep; count: number }[];
  /** Active customers who finished registering. */
  total_customers: number;
  /** Registrations finished per GMT day, seven days, oldest first. */
  registered_7d: number[];
}

/** How far a registration got: phone confirmed, ID check, details, PIN, done. */
export const WALL_REG_STEPS = [
  'phone_verified',
  'id_check',
  'details',
  'pin',
  'completed',
] as const;
export type WallRegStep = (typeof WALL_REG_STEPS)[number];

/**
 * One transaction on the contact wall. `person` is already masked on the
 * server ("Kwame A. ··47"); the exact amount is allowed, an id is not.
 */
export interface WallTxLine {
  at: string;
  /** "Add money · MTN", "Send to MoMo", "Pay bill · ECG". */
  type: string;
  person: string;
  amount_minor: number;
  currency: string;
  status: 'completed' | 'failed' | 'pending';
  /** Plain words for a failure ("Bank refused"); null otherwise. */
  reason: string | null;
}

/** One registration on the contact wall; `person` is masked like a transaction's. */
export interface WallRegLine {
  at: string;
  person: string;
  step: WallRegStep;
  status: 'completed' | 'in_progress' | 'stopped';
}

/** The issue report block: counts, what customers raised in 24 h, who is waiting. */
export interface WallIssuesReport {
  overdue: number;
  open: number;
  resolved_today: number;
  /** The creation time of the oldest open issue. */
  oldest_waiting_at: string | null;
  unassigned: number;
  /** Top 4 by count. */
  categories_24h: { category: string; count: number }[];
  /** Up to 3 overdue or unassigned issues, oldest first. */
  needs_now: {
    issue_no: number;
    waiting_since: string;
    /** Past its due time (otherwise it is only unassigned). */
    overdue: boolean;
    category: string;
    person: string;
    /** Initials, or null when nobody has it. */
    assignee: string | null;
  }[];
}

/** The last 60 minutes in five numbers. */
export interface WallLastHour {
  transactions: number;
  /** completed / (completed + system failures), one decimal; null with nothing finished. */
  success_pct: number | null;
  failed: number;
  registrations: number;
  new_issues: number;
}

/** Everything the contact centre wall shows: figures, plus masked people and issue numbers. */
export interface WallContact {
  generated_at: string;
  issues: {
    overdue: number;
    open: number;
    due_soon: number;
    unassigned: number;
    /** The due time of the longest-overdue open issue. */
    oldest_waiting_at: string | null;
  };
  /** Up to 6 lines: Unassigned first, idle agents left out. */
  queue: WallQueueLine[];
  stuck: {
    count: number;
    oldest_since: string | null;
    at_risk_minor: number;
  };
  kyc: { waiting: number; oldest_since: string | null };
  registrations: WallRegistrations;
  feed: WallFeedLine[];
  /** The last 14 finished or pending transactions, newest first. */
  tx_stream: WallTxLine[];
  /** The 8 registrations with the latest activity. */
  reg_stream: WallRegLine[];
  issues_report: WallIssuesReport;
  last_hour: WallLastHour;
  /** Like ops.status: admin-api adds `switches` (secret and setting) in place of this. */
  switches?: Record<string, { secret: boolean; setting: boolean; on: boolean }>;
  incidents: WallIncident[];
}

/** The SOC wall's bits of the status board: switches, holds, jobs, the chain, outages. */
export interface WallStatus {
  generated_at: string;
  switches?: Record<string, { secret: boolean; setting: boolean; on: boolean }>;
  payouts_on_hold: { kind: string; opened_at: string }[];
  last_books_check: { status: string; at: string | null } | null;
  sweeper: { status: string; at: string | null } | null;
  ledger_chain_head: {
    chain_ok: boolean;
    exported_at: string;
    broken_at: number | null;
  } | null;
  incidents: WallIncident[];
}

/** One person in the referral tree. */
export interface ReferralTreeNode {
  customer: {
    id: string;
    name: string | null;
    phone: string | null;
  };
  /** The referrer; null for a root. */
  parent_id: string | null;
  /** 0 for a root. */
  depth: number;
  /** The referral that brought them in; null for a root. */
  status: (typeof REFERRAL_STATUSES)[number] | null;
  referred_at: string | null;
  qualified_at: string | null;
  direct_count: number;
  total_descendants: number;
  /** Wallet credit earned as a referrer (paid or approved), in minor units. */
  rewards_minor: number;
}

/** One person's referral code, counts and use of the limits. */
export interface ReferrerSummary {
  referrer_id: string;
  name: string | null;
  phone: string | null;
  code: string | null;
  pending: number;
  qualified: number;
  void: number;
  max_per_user: number;
  max_pending_per_user: number | null;
}

export interface KycReview {
  session_id: string;
  purpose: 'registration' | 'reauth';
  document_type: string;
  issuing_country: string;
  started_at: string;
  in_review_since: string;
  /** A decision is on its way to Didit. */
  deciding: boolean;
  /** What Didit said the last time it refused a decision here. */
  last_refusal: string | null;
  customer: CustomerSummary | null;
}

/** One warning Didit raised on a check, in Didit's own words. */
export interface KycWarning {
  /** Didit's code, e.g. `DOCUMENT_EXPIRED`. */
  risk: string;
  /** `error`, `warning` or `information`. */
  level: string | null;
  summary: string | null;
  detail: string | null;
}

/** One check Didit ran (document, liveness, face match, …) and its result. */
export interface KycCheck {
  /** The Didit decision array, e.g. `liveness_checks`. */
  check: string;
  status: string | null;
  /** 0–100 where Didit scores the check. */
  score: number | null;
  warnings: KycWarning[];
}

/** What Didit read off the document. */
export interface KycRead {
  full_name: string | null;
  date_of_birth: string | null;
  document_number: string | null;
  expiry_date: string | null;
  gender: string | null;
}

/**
 * One session for a reviewer: a Didit one as Didit sees it, a Margins (NIA)
 * one as Sao stored it. Every view is audited.
 */
export interface KycDetail {
  session_id: string;
  /** Sao's copy of the status. */
  status: string;
  purpose: 'registration' | 'reauth';
  /** Who checked the ID: Didit, or Margins (NIA, card number + selfie). */
  provider: 'didit' | 'margins';
  /** Margins' answer (`00` verified, `01` not matched, `02` invalid data, `03` NIA watch list); null for Didit. */
  provider_code: string | null;
  /** Margins' transactionGuid; null for Didit. */
  provider_reference: string | null;
  document_type: string;
  issuing_country: string;
  /** True when no reason was given: ID number masked, no photos. */
  masked: boolean;
  started_at: string;
  updated_at: string;
  customer: CustomerSummary | null;
  /**
   * `didit` when read from Didit just now; `stored` when Didit could not be
   * reached, and always for a Margins session (Sao's copy is the only one).
   */
  source: 'didit' | 'stored';
  read: KycRead | null;
  /** Didit's checks, the reasons a session went to review. Empty when unknown. */
  checks: KycCheck[];
  /** Links that expire after `expires_in_seconds`; absent when there is no image. */
  photos: Partial<Record<'id_front' | 'id_back' | 'selfie', string>>;
  expires_in_seconds: number;
}

export interface KycDecisionRow {
  id: string;
  session_id: string;
  new_status: 'Approved' | 'Declined';
  reason: string;
  requested_at: string;
  /** Null while it is being sent. */
  result: 'sent' | 'refused' | null;
  result_detail: string | null;
  staff: string;
  /** The session's status now, as Didit's webhook last set it. */
  session_status: string;
  customer: CustomerSummary | null;
}

export const VERIFICATION_OUTCOMES = [
  'verified',
  'declined',
  'no_answer',
  'liveness',
] as const;

/** One Ghana Card check attempt (Margins / NIA). Sao keeps the code and reference, never Margins' body, so `narration` is Sao's own sentence. */
export interface VerificationAttempt {
  at: string;
  customer: CustomerSummary;
  outcome: (typeof VERIFICATION_OUTCOMES)[number];
  /** 00 verified, 01 not matched, 02 invalid, 03 watch list, 04 no answer, 11 liveness refused. */
  code: string | null;
  narration: string;
  /** Margins' transaction reference; a decline without one is the mark of a Margins or NIA fault. */
  reference: string | null;
  has_reference: boolean;
  /** The nth attempt this person has made. */
  attempt_no: number;
  /** Failed matches (01 or 03) in the 24 hours up to and including this attempt: the daily tries used. */
  tries_24h: number;
  /** Masked on screen; whole in an export. Null for an attempt that left no card number. */
  ghana_card: string | null;
}

export interface VerificationWindowSummary {
  attempts: number;
  verified: number;
  declined: number;
  no_answer: number;
  /** Declines that came back with no Margins reference. */
  without_reference: number;
}

export interface AdminActions {
  'auth.request_link': {
    args: { email: string };
    data: { sent: true };
  };
  'session.me': { args: Record<string, never>; data: SessionMe };
  'session.sign_out': {
    args: Record<string, never>;
    data: { signed_out: true };
  };
  'roles.catalogue': {
    args: Record<string, never>;
    data: { roles: RoleInfo[]; permissions: PermissionInfo[] };
  };
  'staff.list': { args: Record<string, never>; data: StaffMember[] };
  'staff.add': {
    args: { email: string; display_name: string; roles: Role[] };
    data: { staff: StaffMember; invite_sent: boolean };
  };
  'staff.set_roles': {
    args: { staff_id: string; roles: Role[] };
    data: { staff: StaffMember };
  };
  'staff.set_override': {
    args: {
      staff_id: string;
      permission: Permission;
      effect: 'grant' | 'revoke' | 'clear';
    };
    data: { staff: StaffMember };
  };
  'staff.disable': {
    args: { staff_id: string; reason: string };
    data: { staff: StaffMember };
  };
  /** Super admin only: turns a disabled account back on. Sessions stay ended. */
  'staff.enable': {
    args: { staff_id: string; reason: string };
    data: { staff: StaffMember };
  };
  'staff.make_super_admin': {
    args: { staff_id: string; reason: string };
    data: { staff: StaffMember };
  };
  'staff.resend_invite': {
    args: { staff_id: string };
    data: { invite_sent: boolean };
  };
  'staff.reset_mfa': {
    args: { staff_id: string };
    data: { factors_removed: number };
  };
  /**
   * Ghana only, super admin only: whether this person has access in each other
   * country, asked of each country over the signed headquarters link.
   */
  'cells.access': {
    args: { staff_id: string };
    data: CellsAccess;
  };
  /**
   * Ghana only, super admin only, with a fresh authenticator code: makes the
   * person staff in a country with these roles and sends them that country's
   * set-password email. The super admin invites alone: this is an exception
   * to maker-checker, so there is no second approval.
   */
  'cells.invite': {
    args: {
      staff_id: string;
      country: string;
      roles: Role[];
      reason: string;
    };
    data: CellsInviteAnswer;
  };
  /** Ghana only, super admin only, with a fresh authenticator code: turns the person off in a country. */
  'cells.remove': {
    args: { staff_id: string; country: string; reason: string };
    data: { outcome: 'sent'; country: string; status: CountryStatus };
  };
  /**
   * Ghana only: the countries the portal's picker may show this person. Every
   * Ghana staff member sees the ones that are on; a super admin also sees the
   * ones that are off. Wallet Staging has every country on.
   */
  'countries.open': {
    args: Record<string, never>;
    data: CountriesOpen;
  };
  /**
   * Ghana only, no session: the countries that are on, for the sign-in page's
   * picker. Never a country that is off, and `sees_off` is always false.
   * Wallet Staging has every country on. If Ghana cannot read its list it
   * answers no countries. Rate-limited by address.
   */
  'countries.public': {
    args: Record<string, never>;
    data: CountriesOpen;
  };
  /**
   * Ghana only, super admins and staff holding countries.manage: each other
   * country's state, who last changed it and why, its own market switch and
   * what is still missing there, as the country itself answers.
   */
  'countries.list': {
    args: Record<string, never>;
    data: CountriesList;
  };
  /**
   * Ghana only, super admins and staff holding countries.manage, alone (no
   * change request, no second approver), with a reason and an authenticator
   * code typed in the last five minutes (a passkey alone does not count). On
   * sets the country's market switch first, then lists it; Off unlists it
   * first, then asks the country to switch its market off (MONEY_STILL_ON
   * when it cannot be reached).
   */
  'countries.switch': {
    args: { country: string; on: boolean; reason: string };
    data: CountrySwitchAnswer;
  };
  'audit.list': {
    args: AuditFilters;
    data: {
      rows: AuditEntry[];
      next_before_id: number | null;
      /** How many rows match the filters, across every page. */
      total?: number;
    };
  };
  'audit.verify': {
    args: Record<string, never>;
    data: {
      chain_ok: boolean;
      broken_at: number | null;
      last_export: {
        exported_at: string;
        head_id: number | null;
        chain_ok: boolean;
        previous_head_ok: boolean | null;
      } | null;
    };
  };
  'customers.search': {
    args: { q: string };
    data: {
      kind: 'phone' | 'wallet_number' | 'id' | 'reference' | 'name';
      rows: CustomerSummary[];
    };
  };
  /** The super admin acts at once (configuration with a fresh authenticator code); the same checks as a change request. */
  'customers.act': {
    args: {
      kind: (typeof OWNER_KINDS)[number];
      payload: Row;
      reason: string;
    };
    data: { kind: string; result: string; summary: string };
  };
  /** Newest first; archived customers are left out unless `status` is "archived". */
  'customers.list': {
    args: {
      status?: (typeof CUSTOMER_STATUSES)[number];
      attention?: (typeof CUSTOMER_ATTENTION)[number];
      /** When the customer joined: a timestamp, or a UTC day (YYYY-MM-DD). Both ends included. */
      from?: string;
      to?: string;
      before?: string;
      limit?: number;
    };
    data: {
      rows: CustomerListRow[];
      next_before: string | null;
      /** How many rows match the filters, across every page. */
      total?: number;
      summary: CustomerDirectorySummary;
    };
  };
  'customers.get': { args: { customer_id: string }; data: CustomerProfile };
  'customers.tab': {
    args: { customer_id: string; tab: CustomerTab } & Row;
    data: unknown;
  };
  'customers.statement': {
    args: { customer_id: string; wallet_id: string; from: string; to: string };
    data: Row;
  };
  'customers.reveal_phone': {
    args: { customer_id: string; reason: string };
    data: { phone: string };
  };
  /** One reveal for everything the viewer may see; fields they may not see are absent. */
  'customers.reveal_details': {
    args: { customer_id: string; reason: string };
    data: CustomerDetails;
  };
  'customers.reveal_identity': {
    args: { customer_id: string; reason: string };
    data: {
      full_name: string;
      date_of_birth: string;
      id_number: string;
      id_expiry: string | null;
      /** Links that expire after `expires_in_seconds`. */
      documents: Partial<
        Record<'id_front' | 'id_back' | 'selfie' | 'portrait', string>
      >;
      expires_in_seconds: number;
    };
  };
  'transactions.get': {
    args: { transaction_id: string };
    data: TransactionDetail;
  };
  /** Newest first. `before` is the previous page's `next_before`. */
  'transactions.list': {
    args: {
      /** A ledger kind, or `bill_payment` (a payout that is a bill; `payout` then leaves bills out). */
      kind?: string;
      status?: string;
      /** Finds a transaction by its id or any of its references (4 to 80 characters, any case). */
      reference?: string;
      /** A day, YYYY-MM-DD, included. */
      from?: string;
      /** A day, YYYY-MM-DD, included. */
      to?: string;
      before?: string;
      limit?: number;
    };
    data: {
      rows: LiveTransaction[];
      next_before: string | null;
      /** How many rows match the filters, across every page. */
      total?: number;
      summary: TransactionSummary;
    };
  };
  'issues.list': {
    args: IssueFilters;
    data: {
      rows: IssueRow[];
      next_before: string | null;
      /** How many rows match the filters, across every page. */
      total?: number;
      counts: { open: number; overdue: number; due_soon: number; mine: number };
    };
  };
  'issues.get': { args: { issue_id: string }; data: IssueDetail };
  'issues.workers': {
    args: Record<string, never>;
    data: { id: string; name: string }[];
  };
  'issues.assign': {
    args: { issue_id: string; owner_id: string | null };
    data: IssueRow;
  };
  'issues.set_status': {
    args: { issue_id: string; status: IssueStatus; note?: string };
    data: IssueRow;
  };
  'issues.add_note': {
    args: { issue_id: string; note: string };
    data: { added: true };
  };
  'issues.attach': {
    /** `content` is the file as base64; admin-api stores it. */
    args: {
      issue_id: string;
      name: string;
      content_type: string;
      content: string;
    };
    data: { file_id: string; stored: boolean };
  };
  'issues.file_link': {
    args: { file_id: string };
    data: { url: string | null; name: string; expires_in_seconds: number };
  };
  /** The pool's unexplained credit lines a credit can be funded from, then the ones already used. */
  'credits.lines': {
    args: { currency?: string };
    data: { rows: CreditLine[] };
  };
  'credits.attach': {
    /** `content` is the bank statement as base64; admin-api stores it. */
    args: {
      name: string;
      content_type: string;
      content: string;
    };
    data: { file_id: string; stored: boolean };
  };
  'credits.file_link': {
    args: { file_id: string };
    data: { url: string | null; name: string; expires_in_seconds: number };
  };
  'ops.status': { args: Record<string, never>; data: Row };
  /** Payments by rail, 15-minute buckets and failures by code, for the contact wall. */
  'wall.payments': { args: Record<string, never>; data: WallPayments };
  /** Success rate and last success or failure per provider, for the SOC wall. */
  'wall.providers': { args: Record<string, never>; data: WallProviders };
  /** Probe-based state, latency and uptime per platform service, for the SOC wall. */
  'wall.services': { args: Record<string, never>; data: WallServices };
  /** The contact wall's issues, queue, stuck money, ID checks and feed. Needs only wall.view. */
  'wall.contact': { args: Record<string, never>; data: WallContact };
  /** The SOC wall's switches, jobs, ledger chain and outages. Needs only wall.view. */
  'wall.status': { args: Record<string, never>; data: WallStatus };
  /** Counts of account-security events in the last hour and day. */
  'wall.security': { args: Record<string, never>; data: WallSecurity };
  'money.pool_checks': { args: { from?: string; to?: string }; data: RowsPage };
  /** Asks the sweeper to read the Access pool statement now. One request waits at a time, two minutes apart. */
  'pool.refresh': {
    /** `settlement` also reads the Hubtel collections balance. */
    args: { scope?: 'pool' | 'settlement' };
    data: {
      request_id: string;
      requested_at: string;
      status: 'queued' | 'pending';
    };
  };
  /** Whether a pool.refresh was served, and when the statement was last read whole. */
  'money.pool_refresh_state': {
    args: { request_id: string };
    data: {
      done: boolean;
      result: 'read' | 'failed' | null;
      requested_at: string;
      done_at: string | null;
      statement_read_at: string | null;
    };
  };
  /** The latest Hubtel collections balance read, and what that account owes the pool. */
  'money.hubtel_collection': {
    args: Record<string, never>;
    data: {
      read: boolean;
      currency?: string;
      balance_minor?: number;
      owed_minor?: number | null;
      result?: 'ok' | 'mismatch';
      checked_at?: string;
    };
  };
  'money.statement_lines': {
    args: { class?: string; day?: string; unbooked?: boolean };
    data: RowsPage;
  };
  /** Card payments newest first (default the last 14 days, up to 93), with the period's figures. `before` is the previous page's `next_before`. TEST payments are hidden unless `show_test`. */
  'money.cards': {
    args: {
      /** A business day, YYYY-MM-DD. */
      from?: string;
      /** A business day, YYYY-MM-DD, included. */
      to?: string;
      status?: CardPayment['status'];
      /** Only credited payments the bank has not settled. */
      unsettled?: boolean;
      show_test?: boolean;
      before?: string;
      limit?: number;
    };
    data: {
      rows: CardPayment[];
      next_before: string | null;
      summary: CardPaymentsSummary;
    };
  };
  /** Pool credits that could be a card settlement (unexplained, or recognised as one), not booked, newest first. */
  'money.card_settlement_lines': {
    args: { currency?: string; limit?: number };
    data: { rows: CardSettlementLine[] };
  };
  /** The oldest credited LIVE unsettled payments that fit under a pool credit, and how far off they are. */
  'money.card_settlement_suggest': {
    args: { line_id: string };
    data: {
      rows: CardSettlementSuggestedPayment[];
      count: number;
      total_minor: number;
      line_amount_minor: number;
      /** The line less the total; above zero when payments remain that the credit may cover. */
      difference_minor: number;
      currency: string;
    };
  };
  'money.breaks': { args: { status?: string; kind?: string }; data: RowsPage };
  /** One person closes a break that moves no money, with a reason and a fresh authenticator code. */
  'breaks.close': {
    args: { break_id: string; reason: string };
    data: { break_id: string; kind: string };
  };
  /** The super admin closes any open break alone, with a reason and a fresh authenticator code. */
  'breaks.close_any': {
    args: { break_id: string; reason: string };
    data: { break_id: string; kind: string };
  };
  'money.stuck': { args: Record<string, never>; data: RowsPage };
  'money.suspense': { args: Record<string, never>; data: RowsPage };
  'money.provisioning': { args: Record<string, never>; data: RowsPage };
  'money.core_balances': { args: Record<string, never>; data: RowsPage };
  'money.books': { args: { day?: string }; data: RowsPage };
  'money.bills': {
    args: { from?: string; to?: string; issues_only?: boolean };
    data: RowsPage;
  };
  'money.fee_income': {
    args: { from?: string; to?: string; currency?: string };
    data: RowsPage;
  };
  /** Per day, direction (`in` top-ups credited, `out` payouts paid) and network (MTN, TELECEL, BANK): Sao's books against the pool statement, matched by the bank's reference; `totals` per direction and network, `flow` (in, out, what the pool holds against the wallets) and `hubtel` (what the float has paid out). */
  'money.settlement': {
    args: { from?: string; to?: string };
    data: RowsPage;
  };
  /** One day's pulls with no statement line and statement lines no pull accounts for. */
  'money.untraced': { args: { day?: string }; data: RowsPage };
  /** Reversals and refunds, newest first; `total` and `next_before` page it. */
  'money.reversals': {
    args: {
      source?: 'automatic' | 'staff';
      kind?: 'transfer' | 'bill' | 'momo' | 'bank' | 'topup' | 'other';
      /** A day, YYYY-MM-DD, included. */
      from?: string;
      /** A day, YYYY-MM-DD, included. */
      to?: string;
      before?: string;
      limit?: number;
    };
    data: RowsPage;
  };
  /** Hubtel's money to the Access pool: what is owed, the checks, the floor, and the sweep history. */
  'sweeps.overview': {
    args: { before?: string; limit?: number };
    data: SweepsOverview;
  };
  /** One line per day: what was asked and booked, fees, the checks, the books difference, and whether a person signed the day. */
  'sweeps.report_daily': {
    args: { from?: string; to?: string };
    data: SweepDailyReport;
  };
  /** The ids of the caller's passkeys that were confirmed with an authenticator code. */
  'passkeys.mine': {
    args: Record<string, never>;
    data: { confirmed: string[] };
  };
  /** The owner confirms one of their own passkeys with a code from the last five minutes. Once. */
  'passkeys.confirm': {
    args: { passkey_id: string };
    data: { passkey_id: string };
  };
  /** Whether automatic sweeps run, why they are paused, today's count and the last events. */
  'sweeps.auto': {
    args: Record<string, never>;
    data: SweepAutoState;
  };
  /** One person resumes paused automatic sweeps, with a reason and a fresh second factor. */
  'sweeps.resume': {
    args: { reason: string };
    data: { resumed: boolean };
  };
  /** The sweep policy in force and every version before it. A change is a `sweep_policy` change request. */
  'sweeps.policy': {
    args: Record<string, never>;
    data: SweepPolicyPage;
  };
  /** A person with the approve permission signs a finished day with activity off. Once; final. */
  'sweeps.signoff': {
    args: { day: string; note?: string };
    data: { day: string };
  };
  /** What a person must look at: held, unknown, slow, unexplained and failed, with the next step. */
  'sweeps.report_exceptions': {
    args: Record<string, never>;
    data: { rows: SweepException[] };
  };
  /** Every sweep in a date range with its timeline, fee and the statement line it matched. */
  'sweeps.report_ledger': {
    args: { from?: string; to?: string; before?: string; limit?: number };
    data: { rows: SweepLedgerRow[]; next_before: string | null };
  };
  /** Asks for a sweep of up to what Hubtel owes the pool. A different person approves it; the super admin, with a fresh second factor, is approved at once. */
  'sweeps.request': {
    args: { amount_minor: number; reason: string };
    data: { id: string };
  };
  /** A different person approves a sweep, stating the amount they reviewed. Needs a fresh second factor. */
  'sweeps.approve': {
    args: { sweep_id: string; amount_minor: number; reason: string };
    data: { id: string };
  };
  /** Stops a sweep that has not started at Hubtel. */
  'sweeps.cancel': {
    args: { sweep_id: string; reason: string };
    data: { id: string };
  };
  /** Closes an unknown sweep once its break has been resolved. Nothing is booked; `held`: the transfer was done, so it waits for a resend instead. */
  'sweeps.close': {
    args: { sweep_id: string; reason: string };
    data: { id: string; held: boolean };
  };
  /** Sends a refused or untaken Send-To-Bank again under a new reference. One person; needs a fresh second factor. */
  'sweeps.resend': {
    args: { sweep_id: string; reason: string };
    data: { id: string };
  };
  /** Keeps a held sweep's money at Hubtel as float, prepaid by the pool, instead of sending it to Access. One person; needs a fresh second factor. */
  'sweeps.keep': {
    args: { sweep_id: string; reason: string };
    data: { id: string };
  };
  /** Attaches an unexplained Access credit for the sweep's amount, so it can be booked once Hubtel says paid. */
  'sweeps.match': {
    args: { sweep_id: string; line_id: string; reason: string };
    data: { id: string };
  };
  'exports.create': {
    args: { source: AdminAction; args: Row };
    data: {
      export_id: string;
      title: string;
      row_count: number;
      truncated: boolean;
      status: 'building' | 'ready' | 'failed';
      url?: string | null;
      expires_in_minutes?: number;
    };
  };
  'exports.list': { args: Record<string, never>; data: ExportRow[] };
  'exports.link': {
    args: { export_id: string };
    data: { url: string | null; expires_in_minutes: number };
  };
  'changes.propose': {
    args: {
      kind: ChangeKind;
      payload: Row;
      reason: string;
      evidence?: ChangeEvidence[];
    };
    data: ChangeRequest;
  };
  'changes.list': {
    args: {
      view?: 'waiting' | 'mine' | 'all';
      status?: ChangeStatus;
      kind?: ChangeKind;
      customer_id?: string;
      before?: string;
    };
    data: {
      rows: ChangeRequest[];
      next_before: string | null;
      /** How many rows match the filters, across every page. */
      total?: number;
      counts: { waiting: number; mine: number };
    };
  };
  'changes.get': { args: { request_id: string }; data: ChangeDetail };
  'changes.withdraw': { args: { request_id: string }; data: ChangeRequest };
  'changes.approve': {
    args: {
      request_id: string;
      digest: string;
      comment?: string;
      source?: 'own_funds' | 'write_offs';
    };
    data: ChangeRequest;
  };
  'changes.reject': {
    args: { request_id: string; digest: string; comment: string };
    data: ChangeRequest;
  };
  'wallets.freeze_now': {
    args: { wallet_id: string; reason: string };
    data: {
      stop_id: string;
      operator_action_id: string;
      confirm_request_id: string | null;
    };
  };
  /** Super admin only. */
  'killswitch.off': {
    args: { switch: KillSwitch; reason: string };
    data: { switch: KillSwitch; on: false };
  };
  /** Super admin only: switches a surface back on at once, with a reason. */
  'killswitch.on': {
    args: { switch: KillSwitch; reason: string };
    data: { switch: KillSwitch; on: true };
  };
  'staff.link_app': {
    /** An empty phone unlinks. */
    args: { staff_id: string; phone: string };
    data: { linked: boolean };
  };
  'alerts.mine': {
    args: Record<string, never>;
    data: AlertChoices;
  };
  'alerts.set_mine': {
    /** Push always; the two lists say which alerts also come by SMS or email. */
    args: {
      alerts: AlertClass[];
      sms_alerts: AlertClass[];
      email_alerts: AlertClass[];
    };
    data: AlertChoices;
  };
  'fees.list': {
    args: { currency?: string };
    data: {
      currency: string;
      products: FeeProduct[];
      currencies: string[];
      tariffs: FeeTariff[];
    };
  };
  'fees.preview': {
    args: {
      product: FeeProduct;
      currency?: string;
      rail?: string;
      amount_minor: number;
      rules?: FeeRule[];
    };
    data: {
      amount_minor: number;
      currency: string;
      now: FeePrice;
      proposed: FeePrice | null;
    };
  };
  'limits.list': {
    args: Record<string, never>;
    data: { fields: LimitField[]; groups: LimitGroup[] };
  };
  'flags.list': { args: Record<string, never>; data: FeatureFlag[] };
  'flags.set': {
    args: {
      key: string;
      /** Production is for the super admin only; everyone else proposes a change. */
      environment: 'development' | 'preview' | 'production';
      enabled: boolean;
      reason: string;
    };
    data: { key: string; environment: string; enabled: boolean };
  };
  /** The market registry, read only: markets, providers, their accounts by role, and routes. */
  'markets.registry': { args: Record<string, never>; data: MarketRegistry };
  /** The tiers of the market this cell serves. Changing one is a `kyc_tier` change request. */
  'kyc_tiers.list': {
    args: Record<string, never>;
    data: { market: string; rows: KycTierRow[] };
  };
  /**
   * Super admin only: switches this cell's market off at once. Switching it on
   * is a `market_on` change request, approved by a second person.
   */
  'market.off': {
    args: { reason: string };
    data: { market: string; on: false };
  };
  'partners.list': { args: Record<string, never>; data: { rows: Partner[] } };
  'partners.create': {
    args: {
      name: string;
      channel?: string;
      products: PartnerProduct[];
      reason?: string;
    };
    data: Partner;
  };
  /** Changes only the keys given. Going live needs the address list and the certificate fingerprint. */
  'partners.update': {
    args: {
      partner_id: string;
      reason?: string;
      status?: Partner['status'];
      name?: string;
      products?: PartnerProduct[];
      webhook_url?: string | null;
      ip_allowlist?: string[];
      mtls_fingerprint?: string | null;
      name_enquiry_daily_cap?: number;
      rate_per_second?: number;
    };
    data: Partner;
  };
  'partners.channel': {
    args: {
      partner_id: string;
      code: string;
      enabled: boolean;
      reason?: string;
    };
    data: Partner;
  };
  /** Opens a wallet for the partner at the core; staff then fund it with a credit. */
  'partners.open_wallet': {
    args: { partner_id: string; reason?: string };
    data: PartnerWallet;
  };
  'partners.wallets': {
    args: { partner_id: string };
    data: { rows: PartnerWallet[] };
  };
  'partners.issue_credential': {
    args: {
      partner_id: string;
      kind: PartnerCredential['kind'];
      name?: string;
      reason?: string;
    };
    data: IssuedCredential;
  };
  /** Makes the replacement now; the old credential stops after the grace period (setting `partners.rotation_grace_hours`, 24 until set). */
  'partners.rotate_credential': {
    args: {
      credential_id: string;
      kind: PartnerCredential['kind'];
      reason?: string;
    };
    data: IssuedCredential;
  };
  'partners.revoke_credential': {
    args: { credential_id: string; reason?: string };
    data: { id: string; kind: PartnerCredential['kind'] };
  };
  /** The nine certification cases of a sandbox partner, each with when it was first seen. */
  'partners.certification': {
    args: { partner_id: string };
    data: PartnerCertification;
  };
  /** The partner settings (rotation grace, default limits, the go-live rule). Super admin only; not on the general Settings page. */
  'partners.settings': { args: Record<string, never>; data: SettingRow[] };
  'partners.set_setting': {
    args: { key: string; value: unknown; reason?: string };
    data: { key: string; value: unknown };
  };
  /** The partner's 50 newest recorded calls: references, status words, timings; never a body or a name. */
  'partners.activity': {
    args: { partner_id: string };
    data: { rows: PartnerActivity[] };
  };
  'settings.list': { args: Record<string, never>; data: SettingRow[] };
  /** The super admin changes a setting at once (anything but switching to its `stop` needs a fresh second factor); everyone else proposes a `setting` change. */
  'settings.set': {
    args: { key: string; value: unknown; reason: string };
    data: { key: string; value: unknown };
  };
  'bills.catalog': { args: Record<string, never>; data: Biller[] };
  /** Archived notices are left out unless asked for. */
  'notices.list': {
    args: { include_archived?: boolean };
    data: ServiceNotice[];
  };
  'notices.end': {
    args: { notice_id: string; reason: string };
    data: ServiceNotice;
  };
  /** Super admin only: takes an ended notice off the default list. A live one is ended first. */
  'notices.archive': {
    args: { notice_id: string; reason: string };
    data: ServiceNotice;
  };
  /** Messages sent to many customers, newest first, with how many copies went, wait or were cancelled. */
  'messages.campaigns': {
    args: Record<string, never>;
    data: MessageCampaign[];
  };
  /** Super admin only: stops what has not been delivered and withdraws the inbox copies. A push or SMS already sent cannot be recalled. */
  'messages.cancel': {
    args: { request_id: string; reason: string };
    data: { request_id: string; stopped: number; withdrawn: number };
  };
  'messages.templates': {
    args: Record<string, never>;
    data: MessageTemplate[];
  };
  'messages.send': {
    args: {
      customer_id: string;
      template: string;
      sms?: boolean;
      issue_id?: string;
      case_id?: string;
    };
    data: { queued: boolean };
  };
  'messages.segment_count': {
    args: {
      segment: 'everyone' | 'limit_group' | 'customer';
      limit_group?: string;
      customer_id?: string;
    };
    data: { count: number };
  };
  'messages.deliveries': { args: { customer_id: string }; data: Delivery[] };
  /** Every translation on file, by message code then language; empty while Sao speaks English only. */
  'messages.catalogue': {
    args: Record<string, never>;
    data: MessageTranslation[];
  };
  'cases.list': {
    args: {
      status?: CaseStatus | 'active';
      owner?: string;
      customer_id?: string;
    };
    data: { rows: CaseRow[] };
  };
  'cases.get': { args: { case_id: string }; data: CaseDetail };
  'cases.open': {
    args: {
      customer_id: string;
      type: CaseType;
      summary: string;
      transaction_id?: string;
      issue_id?: string;
    };
    data: CaseRow;
  };
  'cases.update': {
    args: {
      case_id: string;
      status?: Exclude<CaseStatus, 'closed'>;
      owner_id?: string;
      note?: string;
      link?: { kind: 'transaction' | 'issue'; ref: string };
      message?: string;
    };
    data: CaseRow;
  };
  'cases.claim': {
    args:
      | {
          case_id: string;
          provider: string;
          reference: string;
          asked_on: string;
        }
      | { claim_id: string; status: CaseClaim['status']; answer?: string };
    data: CaseClaim;
  };
  /** Moves an open case to another customer. Refused while it is linked to the old customer's items. */
  'cases.change_customer': {
    args: { case_id: string; customer_id: string; reason: string };
    data: CaseRow;
  };
  'cases.close': {
    args: { case_id: string; outcome: CaseOutcome; reason: string };
    data: CaseRow;
  };
  'exports.schedules': { args: Record<string, never>; data: ExportSchedule[] };
  'exports.schedule': {
    args: {
      source: AdminAction;
      args?: Row;
      every: 'daily' | 'weekly';
      hour_utc: number;
      weekday?: number;
    };
    data: ExportSchedule;
  };
  'exports.unschedule': {
    args: { schedule_id: string };
    data: ExportSchedule;
  };
  /** Referral rewards, newest first, with the month's budget and (for one person) their cap. */
  'referral_rewards.list': {
    args: {
      status?: ReferralRewardStatus;
      kind?: ReferralReward['kind'];
      role?: ReferralReward['role'];
      beneficiary_id?: string;
      before?: string;
      limit?: number;
    };
    data: {
      rows: ReferralReward[];
      next_before: string | null;
      /** How many rows match the filters, across every page. */
      total?: number;
      summary: ReferralRewardsSummary;
      cap: {
        kind: ReferralRewardsSummary['reward_kind'];
        cap_minor: number | null;
        used_minor: number;
      } | null;
    };
  };
  /** Turns down a reward waiting for approval. Paying one is a `referral_reward` change. */
  'referral_rewards.decline': {
    args: { reward_id: string; reason: string };
    data: ReferralReward;
  };
  /** Every Ghana Card check attempt, newest first. `before` is the previous page's `next_before`. */
  'verifications.attempts': {
    args: {
      outcome?: (typeof VERIFICATION_OUTCOMES)[number];
      customer_id?: string;
      from?: string;
      to?: string;
      before?: string;
      limit?: number;
    };
    data: {
      rows: VerificationAttempt[];
      next_before: string | null;
      /** How many attempts match the filters, across every page. */
      total: number;
      /** The last hour and the last day, whatever the filters say. */
      summary: {
        h1: VerificationWindowSummary;
        h24: VerificationWindowSummary;
      };
    };
  };
  /** Didit sessions in review, oldest first, and the last 25 portal decisions. */
  'kyc.list': {
    args: Record<string, never>;
    data: { rows: KycReview[]; recent: KycDecisionRow[] };
  };
  /** One session's photos, what Didit read and why it went to review. */
  'kyc.detail': {
    /** Without `reason` (10 to 500 characters) the ID number is masked and there are no photos. */
    args: { session_id: string; reason?: string };
    data: KycDetail;
  };
  /** Referrals newest first; `before` is the previous page's `next_before`. `q` is a phone number, a referral code or a customer id. */
  'referrals.list': {
    args: {
      status?: (typeof REFERRAL_STATUSES)[number];
      void_reason?: (typeof REFERRAL_VOID_REASONS)[number];
      referrer_id?: string;
      q?: string;
      before?: string;
      limit?: number;
    };
    data: {
      rows: ReferralRow[];
      next_before: string | null;
      /** How many rows match the filters, across every page. */
      total?: number;
      summary: ReferralSummary;
    };
  };
  'referrals.referrer': {
    args: { referrer_id: string };
    data: ReferrerSummary;
  };
  /** Who brought whom, up to 10 levels. Without `customer_id`: every root and what is below. */
  'referrals.tree': {
    args: { customer_id?: string };
    data: { roots: string[]; nodes: ReferralTreeNode[] };
  };
  /** Voids a pending or qualified referral with reason `staff`; voiding twice changes nothing. */
  'referrals.void': {
    args: { referral_id: string; reason: string };
    data: { referral: ReferralRow; changed: boolean };
  };
  /**
   * Decides a session in review. A Margins (NIA) session is decided in Sao at
   * once; a Didit one was sent to Didit, whose webhook updated the account.
   */
  'kyc.decide': {
    args: {
      session_id: string;
      decision: (typeof KYC_DECISIONS)[number];
      reason: string;
    };
    data: { session_id: string; new_status: string; sent: true };
  };
}

export type AdminAction = keyof AdminActions;
export type ActionArgs<A extends AdminAction> = AdminActions[A]['args'];
export type ActionData<A extends AdminAction> = AdminActions[A]['data'];

export type AdminResponse<A extends AdminAction> =
  | { data: ActionData<A> }
  | { error: AdminError };

/**
 * Who may call each action, for the portal's controls. `null` is any active
 * staff member; `super_admin` is the super admin's flag, which no role
 * grants.
 */
export const ACTION_ACCESS: Record<
  AdminAction,
  { anyOf: readonly (Permission | 'super_admin')[] | null; public?: true }
> = {
  'auth.request_link': { anyOf: null, public: true },
  'session.me': { anyOf: null },
  'session.sign_out': { anyOf: null },
  'roles.catalogue': { anyOf: null },
  'staff.list': { anyOf: ['super_admin', 'staff.disable'] },
  'staff.add': { anyOf: ['super_admin'] },
  'staff.set_roles': { anyOf: ['super_admin'] },
  'staff.set_override': { anyOf: ['super_admin'] },
  'staff.disable': { anyOf: ['super_admin', 'staff.disable'] },
  'staff.enable': { anyOf: ['super_admin'] },
  'staff.make_super_admin': { anyOf: ['super_admin'] },
  'staff.resend_invite': { anyOf: ['super_admin'] },
  'staff.reset_mfa': { anyOf: ['super_admin'] },
  'cells.access': { anyOf: ['super_admin'] },
  'cells.invite': { anyOf: ['super_admin'] },
  'cells.remove': { anyOf: ['super_admin'] },
  'countries.public': { anyOf: null, public: true },
  'countries.open': { anyOf: null },
  'countries.list': { anyOf: ['super_admin', 'countries.manage'] },
  'countries.switch': { anyOf: ['super_admin', 'countries.manage'] },
  'audit.list': { anyOf: ['audit.own', 'audit.all'] },
  'audit.verify': { anyOf: ['audit.all'] },
  'customers.search': { anyOf: ['customers.view'] },
  'customers.list': { anyOf: ['customers.view'] },
  'customers.act': { anyOf: ['super_admin'] },
  'customers.get': { anyOf: ['customers.view'] },
  'customers.tab': { anyOf: ['customers.view'] },
  'customers.statement': { anyOf: ['customers.view'] },
  'customers.reveal_phone': { anyOf: ['customers.reveal_phone'] },
  'customers.reveal_identity': { anyOf: ['customers.reveal_identity'] },
  'customers.reveal_details': {
    anyOf: ['customers.reveal_phone', 'customers.reveal_identity'],
  },
  'transactions.get': {
    anyOf: ['customers.view', 'issues.view', 'money_ops.view'],
  },
  'transactions.list': {
    anyOf: ['customers.view', 'issues.view', 'money_ops.view'],
  },
  'issues.list': { anyOf: ['issues.view'] },
  'issues.get': { anyOf: ['issues.view'] },
  'issues.workers': { anyOf: ['issues.view'] },
  'issues.assign': { anyOf: ['issues.work'] },
  'issues.set_status': { anyOf: ['issues.work'] },
  'issues.add_note': { anyOf: ['issues.work'] },
  'issues.attach': { anyOf: ['issues.work'] },
  'issues.file_link': { anyOf: ['issues.view'] },
  'credits.lines': { anyOf: ['credits.view', 'credits.propose'] },
  'credits.attach': { anyOf: ['credits.propose'] },
  'credits.file_link': {
    anyOf: ['credits.view', 'credits.propose', 'credits.approve'],
  },
  'ops.status': { anyOf: ['money_ops.view', 'killswitch.view'] },
  'wall.payments': {
    anyOf: ['money_ops.view', 'killswitch.view', 'wall.view'],
  },
  'wall.providers': {
    anyOf: ['money_ops.view', 'killswitch.view', 'wall.view'],
  },
  'wall.services': {
    anyOf: ['money_ops.view', 'killswitch.view', 'wall.view'],
  },
  'wall.contact': { anyOf: ['wall.view'] },
  'wall.status': { anyOf: ['wall.view'] },
  'wall.security': { anyOf: ['audit.all'] },
  'money.pool_checks': { anyOf: ['money_ops.view'] },
  'pool.refresh': { anyOf: ['money_ops.view'] },
  'money.pool_refresh_state': { anyOf: ['money_ops.view'] },
  'money.hubtel_collection': { anyOf: ['money_ops.view'] },
  'money.statement_lines': { anyOf: ['money_ops.view'] },
  'money.cards': { anyOf: ['money_ops.view'] },
  'money.card_settlement_lines': { anyOf: ['money_ops.view'] },
  'money.card_settlement_suggest': { anyOf: ['money_ops.view'] },
  'money.breaks': { anyOf: ['money_ops.view'] },
  'breaks.close': { anyOf: ['money_ops.propose'] },
  'breaks.close_any': { anyOf: ['super_admin'] },
  'money.stuck': { anyOf: ['money_ops.view'] },
  'money.suspense': { anyOf: ['money_ops.view'] },
  'money.provisioning': { anyOf: ['money_ops.view'] },
  'money.core_balances': { anyOf: ['money_ops.view'] },
  'money.books': { anyOf: ['money_ops.view'] },
  'money.bills': { anyOf: ['money_ops.view'] },
  'money.fee_income': { anyOf: ['money_ops.view', 'fees.view'] },
  'money.settlement': { anyOf: ['money_ops.view'] },
  'money.untraced': { anyOf: ['money_ops.view'] },
  'money.reversals': { anyOf: ['money_ops.view', 'reversals.view'] },
  'sweeps.overview': { anyOf: ['money_ops.view'] },
  'passkeys.mine': { anyOf: null },
  'passkeys.confirm': { anyOf: null },
  'sweeps.auto': { anyOf: ['money_ops.view'] },
  'sweeps.resume': { anyOf: ['money_ops.approve'] },
  'sweeps.policy': { anyOf: ['money_ops.view'] },
  'sweeps.report_daily': { anyOf: ['money_ops.view'] },
  'sweeps.report_exceptions': { anyOf: ['money_ops.view'] },
  'sweeps.report_ledger': { anyOf: ['money_ops.view'] },
  'sweeps.signoff': { anyOf: ['money_ops.approve'] },
  'sweeps.request': { anyOf: ['money_ops.propose'] },
  'sweeps.approve': { anyOf: ['money_ops.approve'] },
  'sweeps.cancel': { anyOf: ['money_ops.propose', 'money_ops.approve'] },
  'sweeps.close': { anyOf: ['money_ops.approve'] },
  'sweeps.resend': { anyOf: ['money_ops.approve'] },
  'sweeps.keep': { anyOf: ['money_ops.approve'] },
  'sweeps.match': { anyOf: ['money_ops.approve'] },
  'exports.create': { anyOf: ['exports.masked', 'exports.own_cases'] },
  'exports.list': { anyOf: ['exports.masked', 'exports.own_cases'] },
  'exports.link': { anyOf: ['exports.masked', 'exports.own_cases'] },
  'changes.propose': {
    anyOf: [
      'issues.propose',
      'reversals.propose',
      'adjustments.propose',
      'restrictions.propose',
      'money_ops.propose',
      'limits.propose',
      'killswitch.propose',
      'settings.propose',
      'fees.propose',
      'referral_rewards.propose',
      'credits.propose',
    ],
  },
  'changes.list': { anyOf: null },
  'changes.get': { anyOf: null },
  'changes.withdraw': { anyOf: null },
  'changes.approve': {
    anyOf: [
      'issues.approve',
      'reversals.approve',
      'adjustments.approve',
      'restrictions.approve',
      'money_ops.approve',
      'limits.approve',
      'killswitch.approve',
      'settings.approve',
      'fees.approve',
      'referral_rewards.approve',
      'credits.approve',
    ],
  },
  'changes.reject': {
    anyOf: [
      'issues.approve',
      'reversals.approve',
      'adjustments.approve',
      'restrictions.approve',
      'money_ops.approve',
      'limits.approve',
      'killswitch.approve',
      'settings.approve',
      'fees.approve',
      'referral_rewards.approve',
      'credits.approve',
    ],
  },
  'wallets.freeze_now': { anyOf: ['freeze.now', 'super_admin'] },
  'killswitch.off': { anyOf: ['super_admin'] },
  'killswitch.on': { anyOf: ['super_admin'] },
  'staff.link_app': { anyOf: ['super_admin'] },
  'alerts.mine': { anyOf: null },
  'alerts.set_mine': { anyOf: null },
  'fees.list': { anyOf: ['fees.view'] },
  'fees.preview': { anyOf: ['fees.view'] },
  'limits.list': { anyOf: ['limits.view'] },
  'flags.list': { anyOf: ['settings.view'] },
  'flags.set': { anyOf: ['settings.propose', 'super_admin'] },
  'markets.registry': { anyOf: ['settings.view'] },
  'kyc_tiers.list': { anyOf: ['limits.view'] },
  'market.off': { anyOf: ['super_admin'] },
  'partners.list': { anyOf: ['super_admin'] },
  'partners.create': { anyOf: ['super_admin'] },
  'partners.update': { anyOf: ['super_admin'] },
  'partners.channel': { anyOf: ['super_admin'] },
  'partners.open_wallet': { anyOf: ['super_admin'] },
  'partners.wallets': { anyOf: ['super_admin'] },
  'partners.issue_credential': { anyOf: ['super_admin'] },
  'partners.rotate_credential': { anyOf: ['super_admin'] },
  'partners.revoke_credential': { anyOf: ['super_admin'] },
  'partners.certification': { anyOf: ['super_admin'] },
  'partners.activity': { anyOf: ['super_admin'] },
  'partners.settings': { anyOf: ['super_admin'] },
  'partners.set_setting': { anyOf: ['super_admin'] },
  'settings.list': { anyOf: ['settings.view'] },
  'settings.set': { anyOf: ['super_admin'] },
  'bills.catalog': { anyOf: ['settings.view', 'money_ops.view'] },
  'notices.list': { anyOf: ['settings.view'] },
  'notices.end': { anyOf: ['super_admin'] },
  'notices.archive': { anyOf: ['super_admin'] },
  'messages.campaigns': { anyOf: ['settings.view'] },
  'messages.cancel': { anyOf: ['super_admin'] },
  'messages.templates': { anyOf: ['issues.work', 'settings.view'] },
  'messages.send': { anyOf: ['issues.work'] },
  'messages.segment_count': { anyOf: ['settings.propose'] },
  'messages.deliveries': { anyOf: ['customers.view', 'issues.view'] },
  'messages.catalogue': { anyOf: ['settings.view'] },
  'cases.list': { anyOf: ['issues.view'] },
  'cases.get': { anyOf: ['issues.view'] },
  'cases.open': { anyOf: ['issues.work'] },
  'cases.update': { anyOf: ['issues.work'] },
  'cases.claim': { anyOf: ['issues.work'] },
  'cases.close': { anyOf: ['issues.work'] },
  'cases.change_customer': { anyOf: ['issues.work'] },
  'exports.schedules': { anyOf: ['exports.masked', 'exports.own_cases'] },
  'exports.schedule': { anyOf: ['exports.masked', 'exports.own_cases'] },
  'exports.unschedule': { anyOf: ['exports.masked', 'exports.own_cases'] },
  'kyc.list': { anyOf: ['kyc.view', 'kyc.decide'] },
  'verifications.attempts': { anyOf: ['kyc.view', 'kyc.decide'] },
  'kyc.detail': { anyOf: ['kyc.view', 'kyc.decide'] },
  'kyc.decide': { anyOf: ['kyc.decide'] },
  'referral_rewards.list': { anyOf: ['referral_rewards.view'] },
  'referral_rewards.decline': { anyOf: ['referral_rewards.decline'] },
  'referrals.list': { anyOf: ['referrals.view'] },
  'referrals.referrer': { anyOf: ['referrals.view'] },
  'referrals.tree': { anyOf: ['referrals.view'] },
  'referrals.void': { anyOf: ['referrals.void'] },
};

export const ADMIN_ACTIONS = Object.keys(ACTION_ACCESS) as AdminAction[];

export function isAdminAction(value: unknown): value is AdminAction {
  return typeof value === 'string' && Object.hasOwn(ACTION_ACCESS, value);
}

/** Whether a staff member may use an action, by the contract's reading. */
export function canUse(
  action: AdminAction,
  staff: Pick<StaffMember, 'is_super_admin' | 'permissions'>,
): boolean {
  const { anyOf } = ACTION_ACCESS[action];
  if (anyOf === null) return true;
  return anyOf.some((need) =>
    need === 'super_admin'
      ? staff.is_super_admin
      : staff.permissions.includes(need),
  );
}
